import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ItemsSection from './ItemsSection';
import { useOrderLayout } from '../contexts/OrderLayoutContext';
import { AlertCircleIcon, AlertTriangleIcon, CheckIcon } from './icons/LucideIcons';
import PageHeader from './PageHeader';
import OrderFormSection, { SectionLink } from './OrderFormSection';
import SalesTaxNotSetChip from './SalesTaxNotSetChip';
import { useOrderEntryTiming } from '../hooks/useOrderEntryTiming';
import { orderEntryMetricsApi } from '../utils/api';
import RecipientSection from './RecipientSection';
import DeliverySection from './DeliverySection';
import { isMissingPriorityTime, MISSING_PRIORITY_TIME_MESSAGE } from '../utils/priorityFee';
import { staffOrderTax, formatTaxRate } from '../utils/salesTax';
import useStripeTaxQuote, { quotedTax } from '../hooks/useStripeTaxQuote';
import { ordersApi, customersDbApi, mdGiftApi, getErrorMessage } from '../utils/api';
import { searchOrderCustomers, PAYMENT_MODES } from '../utils/orderEntryMode';
import { useOrderEntryMode } from '../hooks/useOrderEntryMode';
import { getCurrentTimeInTimezone, formatDateTimeInTimezone, formatTimeInTimezone, getTodayInTimezone } from '../utils/dateUtils';
import { isTicketOnlyOrder } from '../utils/tickets';
import './orderEntryDark.css';
import { isValidEmail } from '../utils/validation';
import { useHybridDrafts } from '../hooks/useHybridDrafts';
import HybridDraftManager from './HybridDraftManager';
import DraftRecoveryDialog from './DraftRecoveryDialog';
import POSInstructions from './POSInstructions';
import Modal from './Modal';
import StaffMemberSelector from './StaffMemberSelector';
import StaffMemberModal from './StaffMemberModal';
import { useAuth } from '../contexts/AuthContext';
import { useShopIdentity } from '../contexts/ShopIdentityContext';
import { useSettings } from '../hooks/useSettings';
import OrderCreationProgress from './OrderCreationProgress';
import OrderPaymentPanel from './OrderPaymentPanel';
import CustomerHistoryModal from './CustomerHistoryModal';
import { DEFAULT_PRODUCT_TYPE } from '../constants/productTypes';
import SplitButton from './SplitButton';
// import ConvertToEventModal from './ConvertToEventModal'; // Shelved — will revisit
import { workTicketsApi, draftsApi } from '../utils/api';
import VersionFooter from './VersionFooter';

const STORAGE_KEY = 'florist_order_draft';
const AUTO_SAVE_DELAY = 1000; // 1 second

// Phone validation helper - ensures phone has exactly 10 digits (US format)
const validatePhoneNumber = (phone) => {
  if (!phone || !phone.trim()) {
    return { valid: false, error: 'Phone number is required' };
  }

  // Extract only digits from the phone number
  const digits = phone.replace(/\D/g, '');

  // US phone numbers should have exactly 10 digits
  if (digits.length !== 10) {
    return {
      valid: false,
      error: `Phone number must have 10 digits (found ${digits.length})`
    };
  }

  return { valid: true };
};

// Store pickup has no Recipient step. Whoever collects the order is the
// customer by default; "Someone else" (formData.delivery.pickupBySomeoneElse)
// names another person in formData.recipient. With the customer collecting,
// the recipient's name and phones are the customer's. Delivery is unchanged.
// An internal order (formData.internal) has no customer, so its pickup always
// names who collects it, as with "Someone else".
export const pickupBySomeoneElse = (data) =>
  data.delivery?.fulfillmentType === 'pickup' &&
  (data.delivery?.pickupBySomeoneElse === true || data.internal === true);

export const recipientForOrder = (data) => {
  const recipient = data.recipient || {};
  if (data.delivery?.fulfillmentType !== 'pickup' || pickupBySomeoneElse(data)) return recipient;
  const customer = data.customer || {};
  return {
    ...recipient,
    firstName: customer.firstName || '',
    lastName: customer.lastName || '',
    phone: customer.phone || '',
    alternativePhone: customer.alternativePhone,
    alternativePhoneContext: customer.alternativePhoneContext,
  };
};

// The delivery fee a loaded draft restores. Pickup is always $0: drafts saved
// before the pickup fix can hold a zone fee picked up from the address.
export const savedFeeFor = (data) =>
  data?.delivery?.fulfillmentType === 'pickup' ? 0 : (data?.delivery?.savedDeliveryFee || 0);

// Every check the form makes before creating an order, as { fieldPath: message }.
// Shared by submit and the "Before you create" checklist so the two never disagree.
export const validateOrder = ({ formData, user, isTicketOrder, deliveryFee }) => {
  const newErrors = {};
  const recipient = recipientForOrder(formData);

  // Staff account validation - require sub-staff member selection
  if (user?.accountType === 'staff') {
    const savedStaffMember = localStorage.getItem('selectedStaffMember');
    if (!savedStaffMember) {
      newErrors['staffMember'] = 'Please select a staff member before creating an order';
    }
  }

  // Customer validation. An internal order is booked on the shop under a name
  // of its own ("Twigs Holiday Gallery"); a regular order needs a customer.
  if (formData.internal) {
    if (!formData.internalLabel || !formData.internalLabel.trim()) {
      newErrors['internalLabel'] = 'Give the internal order a name';
    }
  } else {
    if (!formData.customer.firstName || !formData.customer.firstName.trim()) {
      newErrors['customer.firstName'] = 'First name is required';
    }
    if (!formData.customer.lastName || !formData.customer.lastName.trim()) {
      newErrors['customer.lastName'] = 'Last name is required';
    }
    // Email is optional, but if provided must be valid
    if (formData.customer.email && formData.customer.email.trim() && !isValidEmail(formData.customer.email)) {
      newErrors['customer.email'] = 'Valid email is required';
    }

    // Customer phone validation - check format and length
    const customerPhoneValidation = validatePhoneNumber(formData.customer.phone);
    if (!customerPhoneValidation.valid) {
      newErrors['customer.phone'] = customerPhoneValidation.error;
    }

    // A house account's phone is its account number: say who called under
    // Placed by, an existing contact ({ id }) or a new one.
    if (formData.customer.isHouseAccount) {
      const contact = formData.customer.contact;
      if (!contact) {
        newErrors['customer.contact'] = 'Say who placed this order';
      } else if (!contact.id) {
        if (!contact.firstName || !contact.firstName.trim()) {
          newErrors['customer.contact.firstName'] = 'First name is required';
        }
        const contactPhone = (contact.phone || '').trim();
        const contactEmail = (contact.email || '').trim();
        if (!contactPhone && !contactEmail) {
          newErrors['customer.contact'] = 'Add a phone or an email for who called';
        }
        if (contactPhone && !validatePhoneNumber(contactPhone).valid) {
          newErrors['customer.contact.phone'] = validatePhoneNumber(contactPhone).error;
        }
        if (contactEmail && !isValidEmail(contactEmail)) {
          newErrors['customer.contact.email'] = 'Valid email is required';
        }
      }
    }
  }

  // Recipient validation (ticket orders have no recipient; the customer stands in.
  // Store pickup by the customer is covered by the customer checks above; pickup
  // by someone else needs that person's name and phone; an internal pickup
  // needs a name only.)
  if (!isTicketOrder && (formData.delivery.fulfillmentType !== 'pickup' || pickupBySomeoneElse(formData))) {
    const phoneOptional = formData.internal === true && formData.delivery.fulfillmentType === 'pickup';
    if (!recipient.firstName || !recipient.firstName.trim()) {
      newErrors['recipient.firstName'] = 'Recipient first name is required';
    }
    if (!recipient.lastName || !recipient.lastName.trim()) {
      newErrors['recipient.lastName'] = 'Recipient last name is required';
    }

    // Require at least one phone number (standard OR alternative)
    const hasStandardPhone = recipient.phone && recipient.phone.trim();
    const hasAlternativePhone = recipient.alternativePhone && recipient.alternativePhone.trim();

    if (!hasStandardPhone && !hasAlternativePhone) {
      if (!phoneOptional) {
        newErrors['recipient.phone'] = 'At least one phone number is required (standard or alternative)';
      }
    } else {
      // Validate format of standard phone if provided
      if (hasStandardPhone) {
        const recipientPhoneValidation = validatePhoneNumber(recipient.phone);
        if (!recipientPhoneValidation.valid) {
          newErrors['recipient.phone'] = recipientPhoneValidation.error;
        }
      }
      // Validate format of alternative phone if provided
      if (hasAlternativePhone) {
        const altPhoneValidation = validatePhoneNumber(recipient.alternativePhone);
        if (!altPhoneValidation.valid) {
          newErrors['recipient.alternativePhone'] = altPhoneValidation.error;
        }
      }
    }
  }

  // Address validation only required for delivery
  if (formData.delivery.fulfillmentType === 'delivery') {
    if (!recipient.address1 || !recipient.address1.trim()) {
      newErrors['recipient.address1'] = 'Address is required';
    }
    if (!recipient.city || !recipient.city.trim()) {
      newErrors['recipient.city'] = 'City is required';
    }
    if (!recipient.state || !recipient.state.trim()) {
      newErrors['recipient.state'] = 'State is required';
    }
    if (!recipient.zip || !recipient.zip.trim()) {
      newErrors['recipient.zip'] = 'ZIP code is required';
    }

    // Validate delivery fee was calculated. A $0 fee is fine when staff typed it
    // into Manual Override, for house accounts (negotiated/comped delivery), and
    // for internal orders (nothing to collect).
    const override = formData.delivery.deliveryFeeOverride;
    const hasManualOverride = override !== undefined && override !== null && override !== '' && !isNaN(parseFloat(override));
    const feeWaived = hasManualOverride || formData.customer.isHouseAccount || formData.internal === true;
    if (!feeWaived && (deliveryFee === 0 || deliveryFee === null || deliveryFee === undefined)) {
      newErrors['delivery.fee'] = 'Delivery fee must be calculated. Please ensure address is complete and valid.';
    }
  }

  // Items validation
  if (!formData.items || formData.items.length === 0) {
    newErrors['items'] = 'At least one item is required';
  } else {
    formData.items.forEach((item, index) => {
      if (!item.name?.trim()) {
        newErrors[`items.${index}.name`] = 'Item name is required';
      }
      // Accept $0.00 (gifts, comps, samples). Reject blank, non-numeric, and negative.
      const priceStr = item.price == null ? '' : String(item.price).trim();
      const priceNum = parseFloat(priceStr);
      if (priceStr === '' || isNaN(priceNum) || priceNum < 0) {
        newErrors[`items.${index}.price`] = 'Enter a valid price ($0.00 or more)';
      }
      if (!item.quantity || parseInt(item.quantity) <= 0) {
        newErrors[`items.${index}.quantity`] = 'Valid quantity is required';
      }
    });
  }

  // Delivery validation
  if (!formData.delivery.date) {
    newErrors['delivery.date'] = 'Delivery date is required';
  }
  if (isMissingPriorityTime(formData.delivery)) {
    newErrors['delivery.priorityTime'] = MISSING_PRIORITY_TIME_MESSAGE;
  }

  return newErrors;
};

const fmtUSD = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);

const LOCATION_LABELS = {
  residential: 'Residence',
  business: 'Business',
  'funeral-home': 'Funeral home',
  hospital: 'Hospital',
  church: 'Church',
  school: 'School',
  hotel: 'Hotel',
};

/**
 * Footer strip of the Recipient step: what the server's quote says about the
 * address (zone, out of area, outside the area) and the delivery fee, with the
 * Manual override that used to sit in the Delivery summary. The quote state
 * comes from DeliverySection (onFeeStatus); the override is
 * formData.delivery.deliveryFeeOverride, which DeliverySection applies.
 */
const DeliveryFeeStrip = ({ status, effectiveFee, locationLabel, override, onOverrideChange }) => {
  const [editing, setEditing] = useState(false);
  const { isCalculating = false, zoneInfo = null, calculatedFee = 0 } = status || {};
  const hasOverride = override !== '' && override != null;
  const showInput = editing || hasOverride;
  const zone = zoneInfo?.zone;
  const extras = [
    zoneInfo?.distance ? `${zoneInfo.distance} mi` : null,
    Number(zone?.order_minimum) > 0 ? `${fmtUSD(Number(zone.order_minimum))} minimum` : null,
  ].filter(Boolean);

  let tone = 'neutral';
  let body;
  if (isCalculating) {
    body = 'Working out the delivery fee…';
  } else if (zoneInfo && !zone) {
    tone = 'danger';
    body = zoneInfo.message || 'Outside the delivery area';
  } else if (zoneInfo?.isExtended) {
    tone = 'warn';
    body = (
      <>
        Outside the standard delivery area{locationLabel ? ` · ${locationLabel}` : ''}{extras.length ? ` · ${extras.join(' · ')}` : ''} ·{' '}
        <strong className="font-semibold">{fmtUSD(effectiveFee)} delivery</strong>
      </>
    );
  } else if (zone) {
    tone = 'ok';
    body = (
      <>
        Address in the delivery area{locationLabel ? ` · ${locationLabel}` : ''} · {zone.name}{extras.length ? ` · ${extras.join(' · ')}` : ''} ·{' '}
        <strong className="font-semibold text-gray-900">{fmtUSD(effectiveFee)} delivery</strong>
      </>
    );
  } else if (calculatedFee > 0 || hasOverride) {
    // The address couldn't be checked; the form fell back to the nearest zone's fee.
    body = (
      <>
        Delivery fee · <strong className="font-semibold text-gray-900">{fmtUSD(effectiveFee)}</strong>
      </>
    );
  } else {
    body = 'The delivery fee is worked out from the address.';
  }

  const toneClass = { ok: 'text-gray-600', warn: 'text-warn-fg', danger: 'text-danger-fg', neutral: 'text-gray-600' }[tone];

  return (
    <div className={`flex flex-wrap items-center gap-x-2.5 gap-y-2 rounded-b-lg border-t border-gray-200 bg-gray-50 px-5 py-3 text-[13px] ${toneClass}`} role="status">
      {tone === 'ok' && <CheckIcon size={14} strokeWidth={3} className="shrink-0 text-dot-ok" />}
      {(tone === 'warn' || tone === 'danger') && <AlertTriangleIcon size={14} className="shrink-0" />}
      <span className="min-w-0">{body}</span>
      {!isCalculating && (calculatedFee > 0 || hasOverride) && (
        <span className="ml-auto flex flex-wrap items-center gap-2">
          {showInput ? (
            <>
              <label className="sr-only" htmlFor="delivery-fee-override">Manual override</label>
              <input
                id="delivery-fee-override"
                type="number"
                step="0.01"
                min="0"
                value={override}
                onChange={(e) => onOverrideChange(e.target.value)}
                placeholder={`Calculated: $${Number(calculatedFee).toFixed(2)}`}
                className="input-field h-ctl-sm w-44 text-base tabular-nums sm:text-base"
                autoFocus={editing && !hasOverride}
              />
              {hasOverride && (
                <span className="field-hint text-xs text-gray-500">
                  Overriding calculated fee of ${Number(calculatedFee).toFixed(2)}
                </span>
              )}
              <SectionLink
                className="!text-xs"
                onClick={() => {
                  setEditing(false);
                  onOverrideChange('');
                }}
              >
                Cancel
              </SectionLink>
            </>
          ) : (
            <SectionLink className="!text-xs" onClick={() => setEditing(true)}>
              Manual override
            </SectionLink>
          )}
        </span>
      )}
    </div>
  );
};

// Formats a US phone as (555) 123-4567, the same as the Recipient step.
const formatUSPhone = (value) => {
  const cleaned = (value || '').replace(/\D/g, '');
  if (cleaned.length === 0) return '';
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

/**
 * Store pickup, under the pickup banner: who collects the order. The customer
 * by default; "Someone else" names another person (formData.recipient), with
 * the same checks as a delivery recipient.
 */
const PickupPersonRow = ({ customer, recipient, someoneElse, internal = false, validationErrors, onSomeoneElse, onSameAsCustomer, onChange }) => {
  const customerLine = [
    `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'The customer',
    customer.phone,
  ].filter(Boolean).join(' · ');
  const err = (f) => validationErrors[`recipient.${f}`];
  const field = (e) => `input-field sm:text-base${e ? ' input-error' : ''}`;

  return (
    <div className="mt-3.5 rounded-md border border-gray-200 px-3.5 py-3">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
        <span className="font-medium text-gray-900">Picked up by</span>
        {!someoneElse && <span className="text-gray-600">{customerLine}</span>}
        {/* An internal order has no customer to fall back to. */}
        {!internal && (
          <span className="ml-auto">
            {someoneElse ? (
              <SectionLink onClick={onSameAsCustomer}>Same as customer</SectionLink>
            ) : (
              <SectionLink onClick={onSomeoneElse}>Someone else</SectionLink>
            )}
          </span>
        )}
      </div>
      {someoneElse && (
        <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-3">
          <div>
            <label className="field-label input-label" htmlFor="pickup-first-name">First name <span className="input-required">*</span></label>
            <input
              id="pickup-first-name"
              name="recipient.firstName"
              type="text"
              value={recipient.firstName || ''}
              onChange={(e) => onChange('firstName', e.target.value)}
              className={field(err('firstName'))}
              autoFocus
            />
            {err('firstName') && <p className="input-error-text">{err('firstName')}</p>}
          </div>
          <div>
            <label className="field-label input-label" htmlFor="pickup-last-name">Last name <span className="input-required">*</span></label>
            <input
              id="pickup-last-name"
              name="recipient.lastName"
              type="text"
              value={recipient.lastName || ''}
              onChange={(e) => onChange('lastName', e.target.value)}
              className={field(err('lastName'))}
            />
            {err('lastName') && <p className="input-error-text">{err('lastName')}</p>}
          </div>
          <div>
            <label className="field-label input-label" htmlFor="pickup-phone">
              Phone {internal
                ? <span className="field-hint font-normal text-gray-500">· optional</span>
                : <span className="input-required">*</span>}
            </label>
            <input
              id="pickup-phone"
              name="recipient.phone"
              type="tel"
              value={recipient.phone || ''}
              onChange={(e) => onChange('phone', formatUSPhone(e.target.value))}
              className={`${field(err('phone'))} tabular-nums`}
              placeholder="(555) 123-4567"
              maxLength="14"
            />
            {err('phone') && <p className="input-error-text">{err('phone')}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

// The order-created dialog. A module-level component on purpose: defined
// inside OrderForm it was a new component type on every form render, so any
// re-render (a toast closing, the order summary updating) remounted it and the
// Take payment panel inside it: the card form reloaded mid-entry and the
// pay-link tab snapped back to Enter card. onDone closes it and resets the form.
function OrderCreatedModal({ open, orderSuccess, onDone, onOpenInvoice }) {
  const [countdown, setCountdown] = useState(3);
  const [autoRedirectEnabled, setAutoRedirectEnabled] = useState(false); // Disable auto-redirect by default to prevent double launches
  // The payment panel below reports what has been paid, so the Payment line
  // can say Paid / part paid instead of "take payment" after a card goes
  // through (QA C P2).
  const [payState, setPayState] = useState(null);
  const paidSoFar = payState ? Number(payState.paid || 0) : 0;
  const stillDue = payState ? Number(payState.balanceDue || 0) : null;

  // Auto-redirect countdown (only if Shopify URL exists)
  useEffect(() => {
    const shopifyUrl = orderSuccess?.shopifyOrderUrl || orderSuccess?.shopifyDraftUrl;
    if (!open || !shopifyUrl || !autoRedirectEnabled || orderSuccess?.internal) return;

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          // Redirect now
          window.open(shopifyUrl, '_blank');
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open, orderSuccess?.shopifyOrderUrl, orderSuccess?.shopifyDraftUrl, orderSuccess?.internal, autoRedirectEnabled]);

  if (!open || !orderSuccess) return null;

  return (
    <div className="fixed inset-0 bg-[color:var(--c-backdrop)] flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-screen overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mr-4">
              <svg className="w-6 h-6 text-floral-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Order Created Successfully!</h3>
              <p className="text-gray-500">
                {orderSuccess.paymentMode === PAYMENT_MODES.COUNTER || orderSuccess.internal ? 'Order' : 'Shopify Order'} {orderSuccess.shopifyOrderName || orderSuccess.orderNumber}
              </p>
            </div>
          </div>

          {/* Auto-redirect controls */}
          {(orderSuccess.shopifyOrderUrl || orderSuccess.shopifyDraftUrl) && !autoRedirectEnabled && (
            <div className="bg-gray-50 p-3 rounded-md border border-gray-200 mb-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-700">Want automatic redirect to Shopify?</p>
                <button
                  onClick={() => {
                    setAutoRedirectEnabled(true);
                    setCountdown(3); // Reset countdown
                  }}
                  className="text-floral-600 hover:text-floral-800 text-sm underline"
                >
                  Enable Auto-Redirect
                </button>
              </div>
            </div>
          )}

          {/* Auto-redirect countdown */}
          {(orderSuccess.shopifyOrderUrl || orderSuccess.shopifyDraftUrl) && autoRedirectEnabled && countdown > 0 && (
            <div className="bg-floral-50 p-4 rounded-md border border-floral-200 mb-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-6 h-6 bg-floral-700 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {countdown}
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-floral-800">
                    Opening Shopify order in <strong>{countdown} second{countdown !== 1 ? 's' : ''}</strong>...
                  </p>
                </div>
                <button
                  onClick={() => setAutoRedirectEnabled(false)}
                  className="ml-auto text-floral-600 hover:text-floral-800 text-sm underline"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="space-y-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-2">Order Details</h4>
              <div className="space-y-1 text-sm">
                <p><span className="font-medium">Order:</span> {orderSuccess.orderNumber}</p>
                {orderSuccess.orderedBy?.name && (
                  <p><span className="font-medium">Placed by:</span> {orderSuccess.orderedBy.name}</p>
                )}
                {orderSuccess.internal && (
                  <p>Internal order. No Shopify order, nothing to collect.</p>
                )}
                {orderSuccess.shopifyOrderName && !orderSuccess.internal && (
                  <p><span className="font-medium">Shopify Order:</span> {orderSuccess.shopifyOrderName}</p>
                )}
                <p><span className="font-medium">Subtotal:</span> ${orderSuccess.subtotal || orderSuccess.total}</p>
                {orderSuccess.deliveryFee > 0 && (
                  <p><span className="font-medium">Delivery Fee:</span> ${orderSuccess.deliveryFee}</p>
                )}
                {orderSuccess.priorityTimeFee > 0 && (
                  <p><span className="font-medium">Priority Time Fee:</span> ${orderSuccess.priorityTimeFee}</p>
                )}
                <p><span className="font-medium">Total:</span> ${orderSuccess.total}</p>
                <p><span className="font-medium">Status:</span> {orderSuccess.status}</p>
                {orderSuccess.paymentMode === PAYMENT_MODES.COUNTER && paidSoFar > 0 && stillDue <= 0 && (
                  <p><span className="font-medium">Payment:</span> Paid.</p>
                )}
                {orderSuccess.paymentMode === PAYMENT_MODES.COUNTER && paidSoFar > 0 && stillDue > 0 && (
                  <p><span className="font-medium">Payment:</span> ${paidSoFar.toFixed(2)} paid, ${stillDue.toFixed(2)} still due.</p>
                )}
                {orderSuccess.paymentMode === PAYMENT_MODES.COUNTER && orderSuccess.printedUnpaid && paidSoFar <= 0 && (
                  <p><span className="font-medium">Payment:</span> Take payment at the counter, then mark the order paid.</p>
                )}
                {orderSuccess.confirmationEmail?.sent && (
                  <p><span className="font-medium">Confirmation:</span> Emailed to {orderSuccess.confirmationEmail.to}</p>
                )}
              </div>
            </div>
            {/* Counter shops on Stripe can take the card right here (renders nothing otherwise).
                No onNotification: a toast re-renders the form, and this modal is defined
                inside it, so the card form would remount. The panel shows its own result. */}
            {orderSuccess.paymentMode === PAYMENT_MODES.COUNTER && orderSuccess.id && !orderSuccess.internal && (
              <OrderPaymentPanel orderId={orderSuccess.id} isAdmin={false} onState={setPayState} />
            )}
          </div>

          <div className="flex flex-col space-y-2">
            {(orderSuccess.shopifyOrderUrl || orderSuccess.shopifyDraftUrl) && (
              <button
                onClick={() => {
                  setAutoRedirectEnabled(false); // Disable auto-redirect to prevent double opening
                  const shopifyUrl = orderSuccess.shopifyOrderUrl || orderSuccess.shopifyDraftUrl;
                  window.open(shopifyUrl, '_blank');

                  onDone();
                }}
                className="w-full bg-floral-700 text-white py-2 px-4 rounded-md hover:bg-floral-800 transition-colors flex items-center justify-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                </svg>
                Open Order in Shopify →
              </button>
            )}

            {orderSuccess.invoiceUrl && (
              <button
                onClick={onOpenInvoice}
                className="w-full bg-floral-700 text-white py-2 px-4 rounded-md hover:bg-floral-800 transition-colors flex items-center justify-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                </svg>
                Open Invoice Link (Not Recommended)
              </button>
            )}
            
            <button
              onClick={() => {
                onDone();
              }}
              className="w-full bg-floral-700 text-white py-2 px-4 rounded-md hover:bg-floral-800 transition-colors"
            >
              Create Another Order
            </button>
            
            <button
              onClick={() => {
                onDone();
              }}
              className="w-full border border-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const OrderForm = forwardRef(({ onOrderCreated, onNotification, onOrderComplete }, ref) => {
  const { orderLayout } = useOrderLayout();
  // Times how long this order took, so the layout comparison rests on numbers
  // rather than on who argues hardest. See the hook for what it does and does
  // not measure.
  const { complete: completeTiming } = useOrderEntryTiming(orderLayout);
  const { user } = useAuth();
  const { settings } = useSettings();
  const shopName = useShopIdentity()?.name || 'This shop';
  // 'counter' = no Shopify: orders are paid at the counter (utils/orderEntryMode)
  const counterPayment = useOrderEntryMode() === PAYMENT_MODES.COUNTER;
  // The layout is the staff member's own choice (Profile → Order entry layout),
  // independent of the appearance; Fable restyles the same markup through its
  // .order-entry overrides in appearance.css.
  //   focused — five numbered steps in one column, summary rail alongside.
  //   classic — the two-column arrangement: Customer + Fulfilment on the left,
  //             Recipient + Card message on the right, Items full width below,
  //             and the same summary rail to the right of all of it.
  const isFocused = orderLayout === 'focused';
  const location = useLocation();
  const routerNavigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [phoneCustomers, setPhoneCustomers] = useState([]);
  const [internalNames, setInternalNames] = useState([]); // the shop's internal customers, for the name suggestions
  const [houseContacts, setHouseContacts] = useState([]); // the picked house account's contacts, for Placed by
  const [showPhoneDropdown, setShowPhoneDropdown] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [priorityTimeFee, setPriorityTimeFee] = useState(0);
  const [showDraftManager, setShowDraftManager] = useState(false);
  const [showPOSInstructions, setShowPOSInstructions] = useState(false);
  const [showLocalRecovery, setShowLocalRecovery] = useState(false);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [showCreationProgress, setShowCreationProgress] = useState(false); // Progress overlay during order creation
  const [taxExemptOverride, setTaxExemptOverride] = useState(null); // null = use customer profile, true/false = manual
  const [showTaxExemptModal, setShowTaxExemptModal] = useState(false); // Confirmation modal
  // const [showConvertToEventModal, setShowConvertToEventModal] = useState(false); // Shelved
  const [showCustomerHistory, setShowCustomerHistory] = useState(false);
  const [isPrintingDraft, setIsPrintingDraft] = useState(false);
  const [isSendingDraftEmail, setIsSendingDraftEmail] = useState(false);
  const [showDraftEmailPreview, setShowDraftEmailPreview] = useState(false);
  const [draftEmailPreviewHtml, setDraftEmailPreviewHtml] = useState('');
  const [isLoadingDraftPreview, setIsLoadingDraftPreview] = useState(false);

  const customerSearchRef = useRef(null);
  // Did the customer agree to order-update texts? Kept outside formData so
  // picking a customer doesn't clear it. Prefilled from their last answer for
  // the same number unless staff already set it for this number.
  const [smsConsent, setSmsConsent] = useState(false);
  const smsConsentSetForRef = useRef(null);
  const customerDropdownRef = useRef(null);
  const phoneInputRef = useRef(null);
  const phoneDropdownRef = useRef(null);
  const formRef = useRef(null);
  const autoSaveTimeoutRef = useRef(null);
  const isSubmittingRef = useRef(false); // Synchronous guard against double-submit
  const [showBackdatedConfirm, setShowBackdatedConfirm] = useState(false);
  const pendingBackdatedSubmitRef = useRef(null); // { source, printedUnpaid }
  const skipBackdatedCheckRef = useRef(false); // Set to true after staff confirms backdated submission

  // Keyboard navigation for customer dropdown
  const [selectedCustomerIndex, setSelectedCustomerIndex] = useState(-1);
  // Keyboard navigation for phone dropdown
  const [selectedPhoneIndex, setSelectedPhoneIndex] = useState(-1);

  const [formData, setFormData] = useState({
    customer: {
      id: null,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      alternativePhone: undefined,
      alternativePhoneContext: undefined,
      defaultAddress: null
    },
    recipient: {
      firstName: '',
      lastName: '',
      address1: '',
      address2: '',
      city: '',
      state: settings.storeState || '', // The shop's own state (see the effect below)
      zip: '',
      phone: ''
    },
    items: [{
      id: Date.now(),
      type: 'custom',
      isProduct: false,
      name: '',
      price: '',
      quantity: 1,
      arrangementType: '',
      colors: [],
      instructions: '',
      productType: DEFAULT_PRODUCT_TYPE
    }],
    delivery: {
      fulfillmentType: 'delivery', // Default to delivery
      date: '',
      time: '',
      locationType: 'residential'
    },
    cardMessage: '',
    specialInstructions: '',
    internal: false,
    internalLabel: ''
  });

  // An empty recipient state (new, reset, cleared or duplicated form) takes
  // the shop's own state from its address. Staff can change it; a shop with
  // no address state leaves it empty and validation asks.
  const shopState = settings.storeState;
  const recipientState = formData.recipient?.state;
  useEffect(() => {
    if (!shopState || recipientState) return;
    setFormData(prev => (!prev.recipient || prev.recipient.state
      ? prev
      : { ...prev, recipient: { ...prev.recipient, state: shopState } }));
  }, [shopState, recipientState]);

  // Hybrid draft system - localStorage for crash protection + server for multi-user access
  const {
    serverDrafts,
    localDraft,
    lastServerSaved,
    isSaving,
    saveStatus,
    loading: draftsLoading,
    saveToServer,
    loadServerDraft,
    deleteServerDraft,
    loadLocalDraft,
    clearLocal,
    getDraftCounts,
    hasLocalRecovery,
    hasContent
  } = useHybridDrafts(formData, {
    enabled: true, // Re-enabled with proper hybrid system
    onSave: (draft) => {
      onNotification?.('Draft saved to server', 'success', 3000);
    },
    onLoad: (data, draft) => {
      if (draft.isLocal) {
        onNotification?.('Local draft recovered', 'success', 3000);
      } else {
        onNotification?.(`Draft loaded: ${draft.customer_name || 'Unnamed'}`, 'success', 3000);
      }
    },
    onError: (error) => {
      onNotification?.('Draft operation failed', 'error', 5000);
    }
  });

  // Show local recovery prompt when available
  useEffect(() => {
    if (hasLocalRecovery() && !showLocalRecovery) {
      setShowLocalRecovery(true);
    }
  }, [hasLocalRecovery, showLocalRecovery]);

  // Test notification system on component mount (temporary)
  useEffect(() => {
    if (onNotification) {
      console.log('OrderForm: onNotification is available');
      // onNotification('OrderForm loaded successfully!', 'info', 2000);
    } else {
      console.log('OrderForm: onNotification is NOT available');
    }
  }, [onNotification]);

  // Reset form function
  const customerPhoneDigits = (formData.customer.phone || '').replace(/\D/g, '');
  useEffect(() => {
    if (smsConsentSetForRef.current === customerPhoneDigits) return;
    smsConsentSetForRef.current = null;
    setSmsConsent(false);
    if (customerPhoneDigits.length !== 10) return undefined;
    let cancelled = false;
    customersDbApi.getSmsConsent(customerPhoneDigits)
      .then((res) => {
        if (!cancelled && smsConsentSetForRef.current === null) setSmsConsent(res?.data?.consented === true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [customerPhoneDigits]);

  // A house account's contacts, for Placed by. Keyed on the local customer id,
  // so a restored draft (which restores selectedCustomer) fetches them again.
  const contactsAccountId = (formData.customer?.isHouseAccount || selectedCustomer?.isHouseAccount)
    ? (selectedCustomer?.localCustomerId || null)
    : null;
  useEffect(() => {
    setHouseContacts([]);
    if (!contactsAccountId) return undefined;
    let cancelled = false;
    customersDbApi.listContacts(contactsAccountId)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setHouseContacts(list);
        // Nobody chosen yet (no viaContact match, no restored choice): start on
        // the account's default contact.
        const fallback = list.find((c) => c.isDefault);
        if (fallback) {
          setFormData((prev) => (prev.customer?.contact
            ? prev
            : { ...prev, customer: { ...prev.customer, contact: { id: fallback.id } } }));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [contactsAccountId]);

  const resetForm = useCallback(() => {
    const emptyFormData = {
      customer: { id: null, firstName: '', lastName: '', email: '', phone: '', alternativePhone: undefined, alternativePhoneContext: undefined, defaultAddress: null },
      recipient: { firstName: '', lastName: '', address1: '', address2: '', city: '', state: '', zip: '', phone: '' },
      items: [{
        id: Date.now(),
        type: 'custom',
        isProduct: false,
        name: '',
        price: '',
        quantity: 1,
        arrangementType: '',
        colors: [],
        instructions: '',
        productType: DEFAULT_PRODUCT_TYPE
      }],
      delivery: { fulfillmentType: 'delivery', date: '', time: '', locationType: 'residential' },
      cardMessage: '',
      specialInstructions: '',
      internal: false,
      internalLabel: ''
    };
    
    setFormData(emptyFormData);
    setDeliveryQuote(null);
    setSmsConsent(false);
    smsConsentSetForRef.current = null;
    setCustomerSearch('');
    setSelectedCustomer(null);
    setShowCustomerDropdown(false);
    setShowCustomerHistory(false);
    setValidationErrors({});
    setOrderSuccess(null);
    setShowSuccessModal(false);

    // Clear auto-save data
    localStorage.removeItem(STORAGE_KEY);
    
    onNotification?.('Form cleared', 'info', 2000);
  }, [onNotification]);

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    saveDraft: () => saveToServer(formData, 'Manual save via ref'),
    loadDraft: (draftId) => loadServerDraft(draftId),
    printWorkTicket: () => {
      // Print functionality would go here
      onNotification?.('Print functionality will be implemented', 'info');
    },
    submitForm: () => {
      if (formRef.current) {
        formRef.current.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    },
    resetForm: () => resetForm(),
    getCurrentData: () => formData,
    hasUnsavedChanges: () => hasContent
  }), [formData, saveToServer, loadServerDraft, hasContent, resetForm, onNotification]);

  // Auto-save to localStorage (legacy support). autosavedAt drives the
  // "Draft saved <time>" indicator in the page header.
  const [autosavedAt, setAutosavedAt] = useState(null);
  const autoSave = useCallback(() => {
    try {
      const timestamp = Date.now();
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        formData,
        customerSearch,
        selectedCustomer,
        timestamp
      }));
      setAutosavedAt(timestamp);
    } catch (error) {
      // Failed to auto-save form data
    }
  }, [formData, customerSearch, selectedCustomer]);

  // Debounced auto-save
  useEffect(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    autoSaveTimeoutRef.current = setTimeout(autoSave, AUTO_SAVE_DELAY);
    
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [autoSave]);

  // Load saved form data on mount
  useEffect(() => {
    // Skip localStorage restore if we're duplicating an order or starting one for a customer
    if (location.state?.duplicateOrder || location.state?.newOrderForCustomer) return;

    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { formData: savedFormData, customerSearch: savedSearch, selectedCustomer: savedCustomer, timestamp } = JSON.parse(saved);

        // Only restore if saved within last 24 hours
        if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
          setFormData(savedFormData);
          setCustomerSearch(savedSearch || '');
          setSelectedCustomer(savedCustomer);

          if (savedFormData && (savedFormData.customer.firstName || savedFormData.customer.email)) {
            onNotification?.('Draft order restored from previous session', 'info', 3000);
          }
        }
      }
    } catch (error) {
      // Failed to load saved form data
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Handle duplicate order data from location.state (via OrderDetails "Duplicate" button)
  useEffect(() => {
    if (!location.state?.duplicateOrder) return;

    const dup = location.state.duplicateOrder;

    setFormData({
      customer: {
        id: null,
        firstName: dup.customer?.firstName || '',
        lastName: dup.customer?.lastName || '',
        email: dup.customer?.email || '',
        phone: dup.customer?.phone || '',
        alternativePhone: dup.customer?.alternativePhone || undefined,
        alternativePhoneContext: dup.customer?.alternativePhoneContext || undefined,
        // A house account's copy still has to say who placed it.
        isHouseAccount: dup.customer?.isHouseAccount || false,
        contact: null,
      },
      recipient: {
        firstName: dup.recipient?.firstName || '',
        lastName: dup.recipient?.lastName || '',
        address1: dup.recipient?.address1 || '',
        address2: dup.recipient?.address2 || '',
        city: dup.recipient?.city || '',
        state: dup.recipient?.state || '',
        zip: dup.recipient?.zip || '',
        phone: dup.recipient?.phone || '',
      },
      items: dup.items?.length > 0 ? dup.items : [{
        id: Date.now(),
        type: 'custom',
        isProduct: false,
        name: '',
        price: '',
        quantity: 1,
        arrangementType: '',
        colors: [],
        instructions: '',
        productType: DEFAULT_PRODUCT_TYPE,
      }],
      delivery: {
        fulfillmentType: dup.delivery?.fulfillmentType || 'delivery',
        date: dup.delivery?.date || '',
        time: dup.delivery?.time || '',
        locationType: dup.delivery?.locationType || 'residential',
        timeCondition: dup.delivery?.timeCondition || '',
        priorityTime: dup.delivery?.priorityTime || '',
        locationInstructions: dup.delivery?.locationInstructions || '',
        windowNotes: dup.delivery?.windowNotes || '',
        pickupTimePeriod: dup.delivery?.pickupTimePeriod || '',
        pickupTime: dup.delivery?.pickupTime || '',
      },
      cardMessage: dup.cardMessage || '',
      specialInstructions: dup.specialInstructions || '',
    });

    // Set customer search field so it shows the customer name
    const customerName = `${dup.customer?.firstName || ''} ${dup.customer?.lastName || ''}`.trim();
    if (customerName) {
      setCustomerSearch(customerName);

      // Re-lookup customer to get full data (house account status, tax exempt, etc.)
      searchOrderCustomers(customerName).then(response => {
        if (response.success && response.data?.length > 0) {
          const exactMatch = response.data.find(c =>
            c.firstName?.toLowerCase() === dup.customer?.firstName?.toLowerCase() &&
            c.lastName?.toLowerCase() === dup.customer?.lastName?.toLowerCase()
          );

          if (exactMatch) {
            setSelectedCustomer(exactMatch);
            setFormData(prev => ({
              ...prev,
              customer: { ...prev.customer, id: exactMatch.id, isHouseAccount: exactMatch.isHouseAccount || prev.customer.isHouseAccount || false },
            }));
          }
        }
      }).catch(err => {
        console.warn('[Duplicate Order] Customer re-lookup failed:', err);
      });
    }

    // Clear location state to prevent re-population on refresh/navigation
    routerNavigate('/create-order', { replace: true, state: {} });

    onNotification?.('Order duplicated - review details and submit when ready', 'info', 5000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // "New order" from the Customers slide-over: preselect that customer and nothing else.
  // The slide-over has the local DB record, but the form keys customers by their search
  // result (Shopify id), so look them up and match on localCustomerId.
  useEffect(() => {
    const target = location.state?.newOrderForCustomer;
    if (!target) return;

    setFormData(prev => ({
      ...prev,
      customer: {
        ...prev.customer,
        firstName: target.firstName || '',
        lastName: target.lastName || '',
        email: target.email || '',
        phone: formatPhoneNumber(target.phone || ''),
      },
    }));

    const query = `${target.firstName || ''} ${target.lastName || ''}`.trim() || target.phone || target.email;
    if (query) {
      searchOrderCustomers(query).then(response => {
        const match = response.success && response.data?.find(c => c.localCustomerId === target.id);
        if (match) handleCustomerSelect(match);
      }).catch(err => {
        console.warn('[New Order] Customer lookup failed:', err);
      });
    }

    // Clear location state to prevent re-population on refresh/navigation
    routerNavigate('/create-order', { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Auto-copy customer phone to recipient when switching to Store Pickup (one-time only)
  const lastFulfillmentTypeRef = useRef(formData.delivery.fulfillmentType);
  useEffect(() => {
    // Only copy when fulfillment type CHANGES to pickup (not on every render)
    if (formData.delivery.fulfillmentType === 'pickup' &&
        lastFulfillmentTypeRef.current !== 'pickup' &&
        formData.customer.phone &&
        !formData.recipient.phone) {
      setFormData(prev => ({
        ...prev,
        recipient: {
          ...prev.recipient,
          phone: prev.customer.phone
        }
      }));
    }
    lastFulfillmentTypeRef.current = formData.delivery.fulfillmentType;
  }, [formData.delivery.fulfillmentType, formData.customer.phone]);

  // Tickets only (event tickets): nothing to deliver or hand over, so the
  // recipient, card message and fulfillment sections step aside. The backend
  // saves the order as a collected pickup with no work ticket.
  const isTicketOrder = isTicketOnlyOrder(formData.items);

  // Stripe Tax mode (the shop opted in, Settings → Sales tax): the tax is the
  // server's (POST /tax/quote, a Stripe Tax calculation for these lines at
  // the delivery address, or the shop's for pickup), never the flat rate.
  // Flat mode never asks.
  const stripeTaxMode = settings.salesTaxMode === 'stripe';
  const formTaxExempt = taxExemptOverride !== null ? taxExemptOverride : (selectedCustomer?.taxExempt || false);
  const taxQuote = useStripeTaxQuote({
    items: formData.items,
    deliveryFee,
    priorityTimeFee,
    fulfillmentType: formData.delivery?.fulfillmentType,
    recipient: formData.recipient,
    exempt: formTaxExempt,
  }, { enabled: stripeTaxMode });
  const settleTaxQuote = taxQuote.settle;

  useEffect(() => {
    if (!isTicketOrder) return;
    setFormData(prev => ({
      ...prev,
      delivery: {
        ...prev.delivery,
        fulfillmentType: 'pickup',
        date: prev.delivery.date || getTodayInTimezone(),
      },
    }));
    setDeliveryFee(0);
    setPriorityTimeFee(0);
  }, [isTicketOrder]);

  // Mother's Day 2026 gift — fetch campaign config once, then keep a
  // complimentary line item in sync with order eligibility in real time.
  // Backend still owns the authoritative auto-add on submit; this is UX.
  const [mdGiftConfig, setMdGiftConfig] = useState(null);
  const mdGiftToastedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    mdGiftApi.getConfig()
      .then(res => {
        if (!cancelled && res?.success && res.data?.enabled && res.data?.gift) {
          setMdGiftConfig(res.data);
        }
      })
      .catch(err => console.warn('[MDGift] config fetch failed:', err.message));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!mdGiftConfig || !mdGiftConfig.gift) return;

    const { eligibleDates, eligibleFulfillment, mdArrangementTag, gift } = mdGiftConfig;
    const date = formData.delivery.date;
    const fulfillment = formData.delivery.fulfillmentType;
    const items = formData.items || [];

    const hasGift = items.some(i => i.is_complimentary === true || i.isComplimentary === true);
    const hasMDItem = items.some(i => Array.isArray(i.tags) && i.tags.includes(mdArrangementTag));
    const hasInventory = typeof gift.inventory_quantity !== 'number' || gift.inventory_quantity > 0;

    const shouldHaveGift =
      eligibleDates.includes(date) &&
      eligibleFulfillment.includes(fulfillment) &&
      hasMDItem &&
      hasInventory;

    if (shouldHaveGift && !hasGift) {
      const giftLine = {
        id: `md-gift-${gift.shopify_variant_id}`,
        type: 'product',
        isProduct: true,
        is_complimentary: true,
        name: gift.name,
        price: '0.00',
        originalPrice: '0.00',
        quantity: 1,
        arrangementType: '',
        colors: [],
        instructions: '',
        image_url: gift.image_url,
        productId: gift.product_id,
        shopify_product_id: gift.shopify_product_id,
        shopify_variant_id: gift.shopify_variant_id,
        tags: ['mothers-day-gift-2026'],
        locked: true, // hint: the gift line shouldn't be hand-edited
      };
      setFormData(prev => ({ ...prev, items: [...(prev.items || []), giftLine] }));
      if (!mdGiftToastedRef.current) {
        mdGiftToastedRef.current = true;
        onNotification?.(`🎁 Complimentary ${gift.name} added`, 'success', 4000);
      }
    } else if (!shouldHaveGift && hasGift) {
      setFormData(prev => ({
        ...prev,
        items: (prev.items || []).filter(i => !(i.is_complimentary === true || i.isComplimentary === true)),
      }));
      onNotification?.(`Complimentary ${gift.name} removed — order no longer qualifies`, 'info', 4000);
      mdGiftToastedRef.current = false;
    }
  }, [mdGiftConfig, formData.delivery.date, formData.delivery.fulfillmentType, formData.items, onNotification]);

  // Save draft and generate a DRAFT-watermarked work ticket PDF
  // Send the draft ticket to the connected print client (routes through the
  // print-logging system like auto-print). If no client is connected (or the
  // send fails), fall back to opening the PDF so staff can print it manually.
  // Returns 'printed' | 'opened'.
  const printDraftTicket = useCallback(async () => {
    try {
      await workTicketsApi.printDraft(formData);
      return 'printed';
    } catch (printErr) {
      console.warn('Print client unavailable, opening PDF for manual print:', printErr.message);
      await workTicketsApi.generateFromDraft(formData);
      return 'opened';
    }
  }, [formData]);

  const handleSaveDraftAndPrint = useCallback(async () => {
    setIsPrintingDraft(true);
    try {
      await saveToServer(formData, 'Draft saved for printing');
      const how = await printDraftTicket();
      if (onNotification) {
        onNotification(
          how === 'printed'
            ? 'Draft saved and work ticket sent to printer'
            : 'Draft saved — no print client connected, opened work ticket for manual printing',
          how === 'printed' ? 'success' : 'warning'
        );
      }
    } catch (err) {
      console.error('Save draft & print failed:', err);
      if (onNotification) onNotification('Failed to save draft and print: ' + err.message, 'error');
    } finally {
      setIsPrintingDraft(false);
    }
  }, [formData, saveToServer, onNotification, printDraftTicket]);

  // Build draft totals from current form state — mirrors the calculation used for real orders
  // so the emailed/previewed estimate reflects the full price the customer will pay.
  // stripeQuote: Stripe Tax mode, the settled quote (taxQuote.settle()).
  const buildDraftTotals = useCallback((stripeQuote = null) => {
    const subtotal = (formData.items || []).reduce(
      (sum, item) => sum + (parseFloat(item.price || 0) * (parseInt(item.quantity, 10) || 1)),
      0
    );
    const isTaxExempt = taxExemptOverride !== null ? taxExemptOverride : (selectedCustomer?.taxExempt || false);
    const salesTaxRate = stripeTaxMode ? (stripeQuote?.rate ?? 0) : (settings.salesTaxRate ?? 0); // unset: no tax, flagged on the form
    const taxAmount = stripeTaxMode
      ? (quotedTax(stripeQuote, isTaxExempt) ?? 0)
      : staffOrderTax({ itemsSubtotal: subtotal, deliveryFee, priorityTimeFee, rate: salesTaxRate, exempt: isTaxExempt });
    return {
      subtotal,
      deliveryFee,
      priorityTimeFee,
      taxAmount,
      salesTaxRate,
      total: subtotal + deliveryFee + priorityTimeFee + taxAmount,
    };
  }, [formData.items, deliveryFee, priorityTimeFee, taxExemptOverride, selectedCustomer, settings.salesTaxRate, stripeTaxMode]);

  // The draft's totals; in Stripe Tax mode once the server has priced it.
  const draftTotals = useCallback(async () => (
    buildDraftTotals(stripeTaxMode ? await settleTaxQuote() : null)
  ), [buildDraftTotals, stripeTaxMode, settleTaxQuote]);

  // Save draft and email a draft invoice to the customer
  const handleEmailDraftInvoice = useCallback(async () => {
    const email = formData?.customer?.email;
    if (!email) {
      if (onNotification) onNotification('No customer email address — add one before sending', 'error');
      return;
    }
    setIsSendingDraftEmail(true);
    try {
      await saveToServer(formData, 'Draft saved before emailing');
      const result = await draftsApi.sendDraftInvoiceEmail(formData, email, await draftTotals());
      if (result.success) {
        if (onNotification) onNotification(`Draft invoice emailed to ${email}`, 'success');
      } else {
        if (onNotification) onNotification('Failed to send draft invoice: ' + (result.reason || 'Unknown error'), 'error');
      }
    } catch (err) {
      console.error('Email draft invoice failed:', err);
      if (onNotification) onNotification('Failed to email draft invoice: ' + err.message, 'error');
    } finally {
      setIsSendingDraftEmail(false);
    }
  }, [formData, saveToServer, onNotification, draftTotals]);

  // Fetch draft invoice HTML and open the staff preview modal (no email is sent yet)
  const handlePreviewDraftInvoice = useCallback(async () => {
    if (!formData.items || formData.items.filter(i => i.name).length === 0) {
      if (onNotification) onNotification('Add at least one item before previewing', 'error');
      return;
    }
    setIsLoadingDraftPreview(true);
    try {
      const result = await draftsApi.previewDraftInvoiceEmail(formData, await draftTotals());
      if (result.success && result.html) {
        setDraftEmailPreviewHtml(result.html);
        setShowDraftEmailPreview(true);
      } else {
        throw new Error(result.message || 'Failed to build preview');
      }
    } catch (err) {
      console.error('Draft invoice preview failed:', err);
      if (onNotification) onNotification('Failed to build preview: ' + err.message, 'error');
    } finally {
      setIsLoadingDraftPreview(false);
    }
  }, [formData, onNotification, draftTotals]);

  // Send from the preview modal — same behavior as handleEmailDraftInvoice, but closes the modal on success
  const handleSendDraftFromPreview = useCallback(async () => {
    await handleEmailDraftInvoice();
    setShowDraftEmailPreview(false);
    setDraftEmailPreviewHtml('');
  }, [handleEmailDraftInvoice]);

  // Save draft, print work ticket, AND email draft invoice
  const handleSaveDraftPrintAndEmail = useCallback(async () => {
    const email = formData?.customer?.email;
    if (!email) {
      if (onNotification) onNotification('No customer email address — add one before sending', 'error');
      return;
    }
    setIsPrintingDraft(true);
    setIsSendingDraftEmail(true);
    try {
      await saveToServer(formData, 'Draft saved for print & email');
      const totals = await draftTotals();
      // Run print (via print client, with manual-open fallback) and email in parallel
      const [printHow, emailResult] = await Promise.all([
        printDraftTicket(),
        draftsApi.sendDraftInvoiceEmail(formData, email, totals),
      ]);
      const printMsg = printHow === 'printed' ? 'printed' : 'opened for manual printing';
      if (emailResult.success) {
        if (onNotification) onNotification(`Draft ${printMsg} and emailed to ${email}`, 'success');
      } else {
        if (onNotification) onNotification(`Draft ${printMsg}, but email failed: ` + (emailResult.reason || 'Unknown error'), 'error');
      }
    } catch (err) {
      console.error('Save, print & email failed:', err);
      if (onNotification) onNotification('Failed: ' + err.message, 'error');
    } finally {
      setIsPrintingDraft(false);
      setIsSendingDraftEmail(false);
    }
  }, [formData, saveToServer, onNotification, draftTotals]);

  // Handle form submission with source parameter
  const handleSubmit = useCallback(async (e, source = 'online_store', printedUnpaid = false) => {
    e.preventDefault();

    // Synchronous guard against double-submit (ref updates immediately, unlike setState)
    if (isSubmittingRef.current || loading) {
      console.log('[OrderForm] Blocked duplicate submission attempt');
      return;
    }
    isSubmittingRef.current = true;

    // Validate form
    console.log('Validating form with data:', formData);
    const newErrors = validateOrder({ formData, user, isTicketOrder, deliveryFee });
    
    if (Object.keys(newErrors).length > 0) {
      console.log('Validation failed with errors:', newErrors);
      setValidationErrors(newErrors);
      onNotification?.('Please fix the errors in the form', 'error');

      // Scroll to first error field
      scrollToFirstError(newErrors);
      isSubmittingRef.current = false; // Reset guard on validation failure
      return;
    }

    console.log('Validation passed, clearing errors');
    setValidationErrors({});

    // Backdated order gate — past delivery dates only allowed for house accounts,
    // and require an explicit confirmation modal so staff don't backdate by accident.
    const todayStr = getTodayInTimezone(); // shop-local, not UTC — evenings flipped the day early
    const isBackdated = formData.delivery?.date && formData.delivery.date < todayStr;
    if (isBackdated && !skipBackdatedCheckRef.current) {
      if (!formData.customer?.isHouseAccount) {
        onNotification?.('Past dates are only allowed for house account customers', 'error');
        isSubmittingRef.current = false;
        return;
      }
      pendingBackdatedSubmitRef.current = { source, printedUnpaid };
      isSubmittingRef.current = false;
      setShowBackdatedConfirm(true);
      return;
    }
    skipBackdatedCheckRef.current = false;

    setLoading(true);
    setShowCreationProgress(true); // Show progress overlay

    try {
      const subtotal = formData.items.reduce((sum, item) => sum + (parseFloat(item.price || 0) * parseInt(item.quantity || 1)), 0);

      // Calculate tax on subtotal + fees (skip for tax-exempt customers)
      const isTaxExempt = taxExemptOverride !== null ? taxExemptOverride : (selectedCustomer?.taxExempt || false);
      let salesTaxRate = settings.salesTaxRate ?? 0; // Not set yet: no tax (the form shows "Sales tax rate not set")
      let taxAmount;
      if (stripeTaxMode) {
        // Stripe Tax mode: the server's tax for exactly these lines (it checks it again on save).
        const quote = await settleTaxQuote();
        if (quote.status !== 'ok' && quote.status !== 'none') {
          onNotification?.(quote.message || 'Sales tax could not be calculated. Try again in a moment.', 'error');
          return;
        }
        taxAmount = quotedTax(quote, isTaxExempt);
        salesTaxRate = quote.rate ?? 0;
      } else {
        taxAmount = staffOrderTax({ itemsSubtotal: subtotal, deliveryFee, priorityTimeFee, rate: salesTaxRate, exempt: isTaxExempt });
      }

      const orderData = {
        ...formData,
        // Pickup: the customer is the recipient (see recipientForOrder).
        recipient: recipientForOrder(formData),
        ...(isTicketOrder && {
          recipient: {
            firstName: formData.customer.firstName,
            lastName: formData.customer.lastName,
            phone: formData.customer.phone,
          },
          cardMessage: '',
        }),
        // Unticked is "not asked", not "no" (the backend ignores it either way).
        customer: { ...formData.customer, smsConsent: smsConsent ? true : null },
        // The quoted zone travels with the order (Phase 2); pickup carries none.
        delivery: {
          ...(({ pickupBySomeoneElse: _formOnly, ...delivery }) => delivery)(formData.delivery),
          ...(formData.delivery?.fulfillmentType === 'delivery' && deliveryQuote ? {
            zoneCode: deliveryQuote.zoneCode,
            distanceMiles: deliveryQuote.distanceMiles,
            latitude: deliveryQuote.latitude,
            longitude: deliveryQuote.longitude,
          } : {}),
        },
        subtotal: subtotal,
        deliveryFee: deliveryFee,
        priorityTimeFee: priorityTimeFee,
        taxAmount: taxAmount,
        taxExempt: isTaxExempt, // the server checks taxAmount against the lines (0 when exempt)
        salesTaxRate: salesTaxRate,
        // Stripe Tax mode: the server recalculates with Stripe and checks it (flat mode sends nothing new).
        ...(stripeTaxMode && { taxMode: 'stripe' }),
        total: subtotal + deliveryFee + priorityTimeFee + taxAmount,
        source: source, // 'online_store' or 'pos'
        printedUnpaid: printedUnpaid, // Flag for Pay Later workflow
        // Add user information for order attribution
        // Priority order: selectedStaffMember (staff account) > authenticated user
        soldBy: (() => {
          // 1. Staff account: Check for selected staff member in localStorage
          if (user?.accountType === 'staff') {
            const savedStaffMember = localStorage.getItem('selectedStaffMember');
            if (savedStaffMember) {
              try {
                const staffMember = JSON.parse(savedStaffMember);
                return {
                  userId: staffMember.id,
                  firstName: staffMember.firstName,
                  lastName: staffMember.lastName,
                  email: staffMember.email || ''
                };
              } catch (e) {
                console.error('Failed to parse selectedStaffMember from localStorage:', e);
              }
            }
          }

          // 3. Default: Use authenticated user
          if (user) {
            return {
              userId: user.id,
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email || ''
            };
          }

          return null;
        })()
      };
      
      console.log('Sending order data to API:', orderData);
      const response = await ordersApi.create(orderData);
      console.log('API response:', response);

      if (response.success) {
        // Clear auto-save data after successful submission
        localStorage.removeItem(STORAGE_KEY);

        // Set success state
        setOrderSuccess(response.order);

        // Record how long this took, on which layout. Fire-and-forget: a
        // metrics failure must never surface to someone who just took an order.
        const timing = completeTiming();
        if (timing) {
          orderEntryMetricsApi.record(timing).catch(() => {});
        }

        // Debug: Log the order response to see what Shopify data we're getting
        console.log('[Order Success] Full response.order:', response.order);
        console.log('[Order Success] shopifyOrderName:', response.order.shopifyOrderName);
        console.log('[Order Success] orderNumber:', response.order.orderNumber);

        // The "Order #… created successfully!" toast comes from the host's
        // onOrderComplete (App.js); a second one here doubled it (QA A F16 / B P2-2).

        // Surface any non-blocking warnings from the Shopify sync
        // (e.g. phone rejected by Shopify but order still created)
        const SHOPIFY_WARNING_MESSAGES = {
          phone_not_synced: "⚠️ Customer's phone wasn't accepted by Shopify and won't sync to their system. Verify with customer.",
          recipient_phone_not_synced: "⚠️ Recipient's phone wasn't accepted by Shopify.",
          customer_unlinked: '⚠️ Order created as a guest in Shopify (customer not linked). Verify contact info.',
          customer_email_conflict:
            '⚠️ This customer has two records in Shopify (phone in one, email in another). ' +
            'The order was linked to the phone record only. ' +
            'To fix: Shopify Admin → Customers → search by email → open the duplicate → ' +
            'click "⋯" → "Merge customer" → pick the phone-record as the surviving customer.'
        };
        (response.order.shopifyWarnings || []).forEach(code => {
          const message = SHOPIFY_WARNING_MESSAGES[code] || `⚠️ Shopify sync warning: ${code}`;
          const duration = code === 'customer_email_conflict' ? 12000 : undefined;
          onNotification?.(message, 'warning', duration);
        });

        // If the backend auto-attached the Mother's Day complimentary gift,
        // surface it so staff know it was added to this order.
        const autoAddedGift = (response.order.items || []).some(i =>
          i.is_complimentary === true || i.isComplimentary === true
        );
        if (autoAddedGift) {
          onNotification?.('🎁 Complimentary Poppy Seed Packet added to this order', 'success', 5000);
        }

        setTimeout(() => {
          setShowSuccessModal(true);
        }, 100);

        onOrderComplete?.(response.order);
      } else {
        throw new Error(response.message || 'Failed to create order');
      }
    } catch (error) {
      console.error('Order creation error:', error);

      // The shop's own number can't be a customer's: show it on the phone field too.
      // On a house account the account's phone is read-only, so it is the
      // caller's (new contact's) phone that was refused.
      if (error?.data?.code === 'SHOP_PHONE') {
        const contactPhone = formData.customer?.isHouseAccount && !formData.customer?.contact?.id
          && (formData.customer?.contact?.phone || '').trim();
        const field = error.data.field || (contactPhone ? 'customer.contact.phone' : 'customer.phone');
        setValidationErrors(prev => ({ ...prev, [field]: error.data.message || getErrorMessage(error) }));
      }

      // If backend returns specific missing fields, display them
      if (error.data && error.data.missingFields && error.data.missingFields.length > 0) {
        const missingFieldsList = error.data.missingFields.join(', ');
        onNotification?.(`Missing required fields: ${missingFieldsList}`, 'error');
      } else {
        onNotification?.(getErrorMessage(error), 'error');
      }
    } finally {
      setLoading(false);
      setShowCreationProgress(false); // Hide progress overlay
      isSubmittingRef.current = false; // Reset synchronous guard
    }
  }, [formData, smsConsent, loading, onNotification, onOrderComplete, deliveryFee, priorityTimeFee, user, completeTiming, isTicketOrder, stripeTaxMode, settleTaxQuote]);

  // Customer search function
  const searchCustomers = useCallback(async () => {
    try {
      const response = await searchOrderCustomers(customerSearch);
      if (response.success) {
        setCustomers(response.data);
        setShowCustomerDropdown(true);
        setSelectedCustomerIndex(-1);
      }
    } catch (error) {
      onNotification?.(getErrorMessage(error), 'error');
    }
  }, [customerSearch, onNotification]);

  // Customer search with debouncing
  useEffect(() => {
    if (customerSearch.length >= 2) {
      const delayedSearch = setTimeout(searchCustomers, 300);
      return () => clearTimeout(delayedSearch);
    } else {
      setCustomers([]);
      setShowCustomerDropdown(false);
    }
  }, [customerSearch, searchCustomers]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+Enter or Cmd+Enter to submit
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (!loading && !showSuccessModal) {
          handleSubmit(e);
        }
      }
      
      // Ctrl+R or Cmd+R to reset form (prevent browser refresh)
      if ((e.ctrlKey || e.metaKey) && e.key === 'r') {
        e.preventDefault();
        if (window.confirm('Reset form? This will clear all current data.')) {
          resetForm();
        }
      }
      
      // Escape to close modals
      if (e.key === 'Escape') {
        if (showSuccessModal) {
          setShowSuccessModal(false);
        } else if (showLocalRecovery) {
          setShowLocalRecovery(false);
        } else if (showDraftManager) {
          setShowDraftManager(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [loading, showSuccessModal, handleSubmit, resetForm]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCustomerDropdown &&
          customerDropdownRef.current &&
          !customerDropdownRef.current.contains(event.target) &&
          customerSearchRef.current &&
          !customerSearchRef.current.contains(event.target)) {
        setShowCustomerDropdown(false);
        setSelectedCustomerIndex(-1);
      }

      if (showPhoneDropdown &&
          phoneDropdownRef.current &&
          !phoneDropdownRef.current.contains(event.target) &&
          phoneInputRef.current &&
          !phoneInputRef.current.contains(event.target)) {
        setShowPhoneDropdown(false);
        setSelectedPhoneIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCustomerDropdown, showPhoneDropdown]);

  const handleCustomerSelect = (customer) => {
    console.log('handleCustomerSelect - customer data:', customer);
    setSelectedCustomer(customer);
    setTaxExemptOverride(null); // Reset override, use new customer's profile setting
    setCustomerSearch(''); // Clear search field after selection
    setFormData(prev => ({
      ...prev,
      customer: {
        id: customer.id,  // Store customer ID for previous recipients feature
        firstName: customer.firstName,
        lastName: customer.lastName,
        email: customer.email,
        phone: formatPhoneNumber(customer.phone), // Format phone number for display
        defaultAddress: customer.defaultAddress || null,
        isHouseAccount: customer.isHouseAccount || false,
        // Found through one of the account's contacts: that is who called.
        contact: customer.isHouseAccount && customer.viaContact?.id ? { id: customer.viaContact.id } : null
      },
      // Auto-populate card message for house accounts with a preset message
      ...(customer.isHouseAccount && customer.presetCardMessage ? { cardMessage: customer.presetCardMessage } : {}),
      // Auto-populate delivery fee override for house accounts with a fixed delivery fee
      ...(customer.fixedDeliveryFee ? {
        delivery: {
          ...prev.delivery,
          deliveryFeeOverride: customer.fixedDeliveryFee.toString()
        }
      } : {})
    }));
    setShowCustomerDropdown(false);
    setSelectedCustomerIndex(-1);
    setCustomers([]); // Clear search results
    setShowCustomerHistory(false); // Close history modal if switching customer

    // Clear any customer validation errors
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors['customer.firstName'];
      delete newErrors['customer.lastName'];
      delete newErrors['customer.email'];
      delete newErrors['customer.contact'];
      delete newErrors['customer.contact.firstName'];
      delete newErrors['customer.contact.phone'];
      delete newErrors['customer.contact.email'];
      return newErrors;
    });
  };

  const handleReorderFromHistory = useCallback((order) => {
    setFormData(prev => ({
      ...prev,
      recipient: {
        firstName: order.recipientFirstName || '',
        lastName: order.recipientLastName || '',
        phone: order.recipientPhone || '',
        address1: order.addressDetail?.address1 || '',
        address2: order.addressDetail?.address2 || '',
        city: order.addressDetail?.city || '',
        state: order.addressDetail?.state || '',
        zip: order.addressDetail?.zip || '',
      },
      items: order.items && order.items.length > 0
        ? order.items.map((item, i) => ({
            id: Date.now() + i,
            type: 'custom',
            isProduct: false,
            name: item.name || '',
            price: item.unitPrice ? item.unitPrice.toFixed(2) : '',
            quantity: item.quantity || 1,
            arrangementType: item.arrangementType || '',
            colors: item.colors || [],
            instructions: item.instructions || '',
            productType: DEFAULT_PRODUCT_TYPE,
          }))
        : prev.items,
      delivery: {
        ...prev.delivery,
        fulfillmentType: order.fulfillmentType || prev.delivery.fulfillmentType,
      },
    }));
    setShowCustomerHistory(false);
    onNotification?.(`Loaded order #${order.orderNumber} — review and update as needed`, 'success', 4000);
  }, [onNotification]);

  const handleCustomerSearchKeyDown = (e) => {
    if (!showCustomerDropdown || customers.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedCustomerIndex(prev => 
          prev < customers.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedCustomerIndex(prev => 
          prev > 0 ? prev - 1 : customers.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedCustomerIndex >= 0) {
          handleCustomerSelect(customers[selectedCustomerIndex]);
        }
        break;
      case 'Escape':
        setShowCustomerDropdown(false);
        setSelectedCustomerIndex(-1);
        break;
      default:
        break;
    }
  };

  // Format phone number with parentheses and dashes
  const formatPhoneNumber = (value) => {
    if (!value) return '';
    // Remove all non-digits
    let cleaned = value.replace(/\D/g, '');

    // If it starts with 1 and has 11 digits, remove the leading 1 (US country code)
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      cleaned = cleaned.slice(1);
    }

    if (cleaned.length === 0) return '';
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6)
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  // Phone search function
  const searchCustomersByPhone = useCallback(async (phoneValue) => {
    try {
      const response = await searchOrderCustomers(phoneValue);
      if (response.success) {
        setPhoneCustomers(response.data);
        setShowPhoneDropdown(true);
        setSelectedPhoneIndex(-1);
      }
    } catch (error) {
      onNotification?.(getErrorMessage(error), 'error');
    }
  }, [onNotification]);

  // Phone search with debouncing
  useEffect(() => {
    const phoneValue = formData.customer.phone;
    // Only search if there are at least 3 digits
    const digits = phoneValue.replace(/\D/g, '');
    // The phone of the customer just picked: don't offer that same customer
    // again under the phone field (QA B P2-2: listed twice).
    const pickedDigits = String(selectedCustomer?.phone || '').replace(/\D/g, '');
    if (pickedDigits && digits === pickedDigits) {
      setPhoneCustomers([]);
      setShowPhoneDropdown(false);
      return undefined;
    }
    if (digits.length >= 3) {
      const delayedSearch = setTimeout(() => searchCustomersByPhone(phoneValue), 300);
      return () => clearTimeout(delayedSearch);
    } else {
      setPhoneCustomers([]);
      setShowPhoneDropdown(false);
    }
  }, [formData.customer.phone, searchCustomersByPhone, selectedCustomer?.phone]);

  const handlePhoneCustomerSelect = (customer) => {
    console.log('handlePhoneCustomerSelect - customer data:', customer);
    setFormData(prev => ({
      ...prev,
      customer: {
        id: customer.id,
        firstName: customer.firstName,
        lastName: customer.lastName,
        email: customer.email,
        phone: formatPhoneNumber(customer.phone), // Format phone number for display
        defaultAddress: customer.defaultAddress || null
      }
    }));
    setShowPhoneDropdown(false);
    setSelectedPhoneIndex(-1);
    setPhoneCustomers([]);

    // Clear any customer validation errors
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors['customer.firstName'];
      delete newErrors['customer.lastName'];
      delete newErrors['customer.email'];
      delete newErrors['customer.phone'];
      return newErrors;
    });

    onNotification?.(`Selected ${customer.firstName} ${customer.lastName}`, 'success', 2000);
  };

  const handlePhoneSearchKeyDown = (e) => {
    if (!showPhoneDropdown || phoneCustomers.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedPhoneIndex(prev =>
          prev < phoneCustomers.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedPhoneIndex(prev =>
          prev > 0 ? prev - 1 : phoneCustomers.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedPhoneIndex >= 0) {
          handlePhoneCustomerSelect(phoneCustomers[selectedPhoneIndex]);
        }
        break;
      case 'Escape':
        setShowPhoneDropdown(false);
        setSelectedPhoneIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleInputChange = (section, field, value) => {
    // Format phone numbers
    if (field === 'phone') {
      value = formatPhoneNumber(value);
    }

    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));

    // Clear validation error for this field
    const fieldKey = `${section}.${field}`;
    if (validationErrors[fieldKey]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldKey];
        return newErrors;
      });
    }
  };

  // Handle recipient changes
  const handleRecipientChange = (recipientData) => {
    setFormData(prev => ({
      ...prev,
      recipient: recipientData
    }));
  };

  // Handle delivery changes
  const handleDeliveryChange = (deliveryData) => {
    setFormData(prev => ({
      ...prev,
      delivery: deliveryData
    }));
  };

  // Auto-fill recipient name when Store Pickup is selected
  useEffect(() => {
    if (formData.delivery.fulfillmentType === 'pickup' &&
        formData.customer.firstName &&
        formData.customer.lastName) {
      // Auto-fill firstName and lastName independently when empty
      const needsFirstName = !formData.recipient.firstName;
      const needsLastName = !formData.recipient.lastName;
      if (needsFirstName || needsLastName) {
        setFormData(prev => ({
          ...prev,
          recipient: {
            ...prev.recipient,
            ...(needsFirstName && { firstName: prev.customer.firstName }),
            ...(needsLastName && { lastName: prev.customer.lastName })
          }
        }));
      }
    }
  }, [formData.delivery.fulfillmentType, formData.customer.firstName, formData.customer.lastName]);

  // Store pickup: who collects it. Decided once per switch to pickup (or per
  // loaded draft/duplicate without the flag): a recipient already named as
  // someone other than the customer stays as "Someone else"; otherwise the
  // customer collects it and the recipient follows the customer's name and
  // phone, so drafts and the saved order carry the right person.
  const pickupFlag = formData.delivery.pickupBySomeoneElse;
  useEffect(() => {
    const isPickupOrder = formData.delivery.fulfillmentType === 'pickup' && !isTicketOnlyOrder(formData.items);
    if (!isPickupOrder) {
      if (pickupFlag !== undefined) {
        setFormData(prev => {
          const { pickupBySomeoneElse: _drop, ...delivery } = prev.delivery;
          return { ...prev, delivery };
        });
      }
      return;
    }
    // Internal: no customer to mirror; the pickup person is typed in.
    if (formData.internal) return;
    const r = formData.recipient || {};
    const c = formData.customer || {};
    const norm = (v) => (v || '').trim();
    if (pickupFlag === undefined) {
      const recipientName = `${norm(r.firstName)} ${norm(r.lastName)}`.trim().toLowerCase();
      const customerName = `${norm(c.firstName)} ${norm(c.lastName)}`.trim().toLowerCase();
      const someoneElse = !!recipientName && recipientName !== customerName;
      setFormData(prev => ({ ...prev, delivery: { ...prev.delivery, pickupBySomeoneElse: someoneElse } }));
      return;
    }
    if (pickupFlag === false &&
        ((r.firstName || '') !== (c.firstName || '') || (r.lastName || '') !== (c.lastName || '') || (r.phone || '') !== (c.phone || ''))) {
      setFormData(prev => ({
        ...prev,
        recipient: {
          ...prev.recipient,
          firstName: prev.customer.firstName || '',
          lastName: prev.customer.lastName || '',
          phone: prev.customer.phone || '',
        },
      }));
    }
  }, [pickupFlag, formData.delivery.fulfillmentType, formData.items, formData.recipient, formData.customer, formData.internal]);

  // Phase 2: what the server quoted for the delivery address (zone, distance,
  // coordinates). Sent with the order so it records the zone and can re-check the fee.
  const [deliveryQuote, setDeliveryQuote] = useState(null);
  // DeliverySection's quote progress and zone, for the fee strip under the Recipient step.
  const [deliveryFeeStatus, setDeliveryFeeStatus] = useState(null);
  const itemsLeadTimeDays = (formData.items || []).reduce((max, item) => Math.max(max, Number(item.leadTimeDays) || 0), 0);

  const handleDeliveryFeeChange = (fee) => {
    setDeliveryFee(fee);
    setFormData(prev => ({
      ...prev,
      delivery: { ...prev.delivery, savedDeliveryFee: fee }
    }));
  };

  // Handle priority time fee changes
  const handlePriorityTimeFeeChange = (fee) => {
    setPriorityTimeFee(fee);
    setFormData(prev => ({
      ...prev,
      delivery: { ...prev.delivery, savedPriorityTimeFee: fee }
    }));
  };

  const handleItemsChange = (newItems) => {
    setFormData(prev => ({
      ...prev,
      items: newItems
    }));

    // Clear validation errors for items
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      // Remove all item-related errors
      Object.keys(newErrors).forEach(key => {
        if (key.startsWith('items.')) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });
  };



  const openInvoiceUrl = () => {
    if (orderSuccess?.invoiceUrl) {
      window.open(orderSuccess.invoiceUrl, '_blank');
    }
  };

  const getFieldError = (fieldPath) => validationErrors[fieldPath];

  // Create human-readable field names for validation summary
  const getFieldLabel = (fieldPath) => {
    const labels = {
      'staffMember': 'Staff Member Selection',
      'customer.firstName': 'Customer First Name',
      'customer.lastName': 'Customer Last Name',
      'customer.email': 'Customer Email',
      'customer.phone': 'Customer Phone',
      'customer.contact': 'Placed by',
      'customer.contact.firstName': 'Placed by First Name',
      'customer.contact.phone': 'Placed by Phone',
      'customer.contact.email': 'Placed by Email',
      'internalLabel': 'Internal order name',
      'recipient.firstName': 'Recipient First Name',
      'recipient.lastName': 'Recipient Last Name',
      'recipient.address1': 'Delivery Address',
      'recipient.city': 'City',
      'recipient.state': 'State',
      'recipient.zip': 'ZIP Code',
      'recipient.phone': 'Recipient Phone',
      'recipient.alternativePhone': 'Recipient Alternative Phone',
      'delivery.date': 'Delivery Date',
      'delivery.priorityTime': 'Delivery Time',
      'delivery.fee': 'Delivery Fee',
      'items': 'Order Items'
    };

    if (labels[fieldPath]) return labels[fieldPath];
    
    // Handle dynamic item fields
    if (fieldPath.startsWith('items.')) {
      const parts = fieldPath.split('.');
      if (parts.length >= 3) {
        const itemIndex = parseInt(parts[1]) + 1;
        const field = parts[2];
        const fieldLabels = { name: 'Name', price: 'Price', quantity: 'Quantity' };
        return `Item ${itemIndex} ${fieldLabels[field] || field}`;
      }
    }

    // Never show a raw key like "recipient.phone" (QA B P2-1): "Recipient phone".
    const words = fieldPath.split('.').join(' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase().trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
  };

  // Validation Summary Component
  const ValidationSummary = () => {
    const errorCount = Object.keys(validationErrors).length;
    console.log('ValidationSummary render - errorCount:', errorCount, 'errors:', validationErrors);
    if (errorCount === 0) return null;

    return (
      <div className="flex gap-2.5 rounded-md bg-danger-bg px-3.5 py-3 text-[13px] text-danger-fg" role="alert">
        <AlertCircleIcon size={16} className="mt-px shrink-0" />
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold">
            Please fix {errorCount} error{errorCount > 1 ? 's' : ''} before creating the order:
          </h3>
          <ul className="mt-1.5 space-y-1">
            {Object.entries(validationErrors).map(([fieldPath, message]) => (
              <li key={fieldPath}>
                <button
                  type="button"
                  className="text-left underline underline-offset-2 hover:no-underline"
                  onClick={() => scrollToFirstError({ [fieldPath]: message })}
                >
                  {getFieldLabel(fieldPath)}: {message}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  // Scroll to first error field
  const scrollToFirstError = (errors) => {
    const errorFields = Object.keys(errors);
    if (errorFields.length === 0) return;

    // Handle staffMember error specially - open staff modal
    if (errorFields.includes('staffMember')) {
      setShowStaffModal(true);
      return;
    }

    // Map field names to likely element selectors
    const fieldMapping = {
      'customer.firstName': '[name="customer.firstName"]',
      'customer.lastName': '[name="customer.lastName"]',
      'customer.email': '[name="customer.email"]',
      'customer.phone': '[name="customer.phone"]',
      'customer.contact': '[data-field="customer.contact"]',
      'customer.contact.firstName': '[data-field="customer.contact.firstName"]',
      'customer.contact.phone': '[data-field="customer.contact.phone"]',
      'customer.contact.email': '[data-field="customer.contact.email"]',
      'internalLabel': '[name="internalLabel"]',
      'recipient.firstName': '[name="recipient.firstName"]',
      'recipient.lastName': '[name="recipient.lastName"]',
      'recipient.address1': '[name="recipient.address1"]',
      'recipient.city': '[name="recipient.city"]',
      'recipient.state': '[name="recipient.state"]',
      'recipient.zip': '[name="recipient.zip"]',
      'delivery.date': '[name="delivery.date"]',
      'delivery.priorityTime': '[name="delivery.priorityTime"]',
      'items': '[data-section="items"]',
    };

    // Try to find and scroll to the first error field
    for (const fieldPath of errorFields) {
      let selector = fieldMapping[fieldPath];

      // Handle dynamic item fields — target the specific input by data-field, not the section header
      if (!selector && fieldPath.startsWith('items.')) {
        selector = `[data-field="${fieldPath}"]`;
      }
      
      if (selector) {
        const element = document.querySelector(selector);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest' 
          });
          // Focus the field if it's focusable
          if (element.focus) {
            setTimeout(() => element.focus(), 300);
          }
          break;
        }
      }
    }
  };

  // Close the order-created dialog and reset the form; staff accounts then get
  // the staff picker (a short delay so the reset lands first).
  const finishOrderCreated = () => {
    setShowSuccessModal(false);
    resetForm();
    if (user && user.accountType === 'staff') {
      setTimeout(() => setShowStaffModal(true), 100);
    }
  };

  // ── Layout values (Forest New order; the same layout in both appearances) ──
  const isPickup = formData.delivery.fulfillmentType === 'pickup';
  // Store pickup has no Recipient step (the customer collects it); tickets need neither.
  const showRecipientStep = !isTicketOrder && !isPickup;
  const stepNumbers = (() => {
    let n = 0;
    const numbers = {
      customer: ++n,
      fulfil: ++n,
      recipient: showRecipientStep ? ++n : null,
    };
    // Numbered in reading order, which differs by layout: Focused runs Items
    // then Card message down the one column; Classic reads Card message in the
    // right column before Items, which sits full width below both columns.
    if (isFocused) {
      numbers.items = ++n;
      numbers.message = isTicketOrder ? null : ++n;
    } else {
      numbers.message = isTicketOrder ? null : ++n;
      numbers.items = ++n;
    }
    return numbers;
  })();
  const isHouseAccount = !!(formData.customer?.isHouseAccount || selectedCustomer?.isHouseAccount);
  // Placed by: the select holds '' (nobody yet), a contact id, or NEW_CONTACT.
  const NEW_CONTACT = '__new';
  const placedBy = formData.customer?.contact || null;
  const isNewContact = !!placedBy && !placedBy.id;
  const placedBySelectValue = !placedBy ? '' : (placedBy.id ? String(placedBy.id) : NEW_CONTACT);
  // The contact the search matched through may not be in the fetched list yet.
  const viaContact = selectedCustomer?.viaContact;
  const contactOptions = viaContact?.id && !houseContacts.some((c) => String(c.id) === String(viaContact.id))
    ? [...houseContacts, viaContact]
    : houseContacts;
  const chosenContact = placedBy?.id ? contactOptions.find((c) => String(c.id) === String(placedBy.id)) : null;
  const showMakeDefault = !!placedBy && (isNewContact || !chosenContact?.isDefault);
  const accountName = `${formData.customer?.firstName || ''} ${formData.customer?.lastName || ''}`.trim();
  const setPlacedByDefault = (checked) => setFormData((prev) => {
    const { isDefault: _was, ...contact } = prev.customer.contact || {};
    return { ...prev, customer: { ...prev.customer, contact: checked ? { ...contact, isDefault: true } : contact } };
  });
  const contactOptionLabel = (c) => {
    const name = `${c.firstName || ''} ${c.lastName || ''}`.trim();
    return c.title ? `${name} · ${c.title}` : name;
  };
  const clearContactErrors = (...keys) => setValidationErrors((prev) => {
    if (!keys.some((k) => prev[k])) return prev;
    const next = { ...prev };
    keys.forEach((k) => delete next[k]);
    return next;
  });
  const choosePlacedBy = (value) => {
    let contact = null;
    if (value === NEW_CONTACT) {
      contact = { firstName: '', lastName: '', phone: '', email: '', title: '' };
    } else if (value) {
      const match = contactOptions.find((c) => String(c.id) === value);
      contact = { id: match ? match.id : value };
    }
    setFormData((prev) => ({ ...prev, customer: { ...prev.customer, contact } }));
    clearContactErrors('customer.contact', 'customer.contact.firstName', 'customer.contact.phone', 'customer.contact.email');
  };
  const setNewContactField = (field, raw) => {
    const value = field === 'phone' ? formatPhoneNumber(raw) : raw;
    setFormData((prev) => ({
      ...prev,
      customer: { ...prev.customer, contact: { ...(prev.customer.contact || {}), [field]: value } },
    }));
    clearContactErrors(`customer.contact.${field}`, ...(field === 'phone' || field === 'email' ? ['customer.contact'] : []));
  };
  const draftCounts = getDraftCounts();

  // Order summary: the same arithmetic the summary rail always used.
  const itemsSubtotal = formData.items.reduce(
    (sum, item) => sum + (parseFloat(item.price) || 0) * (parseInt(item.quantity) || 0),
    0
  );
  // Stripe Tax mode: the server's tax and rate; "Calculating…" until it answers.
  const summaryTaxRate = stripeTaxMode ? taxQuote.rate : (settings.salesTaxRate ?? 0);
  const summaryTax = stripeTaxMode
    ? quotedTax(taxQuote, formTaxExempt)
    : staffOrderTax({ itemsSubtotal, deliveryFee, priorityTimeFee, rate: summaryTaxRate, exempt: formTaxExempt });
  const taxNotYet = taxQuote.status === 'failed' ? '—' : 'Calculating…';

  const nameOf = (p) => `${p?.firstName || ''} ${p?.lastName || ''}`.trim();
  const customerName = nameOf(formData.customer);
  const pickupOther = pickupBySomeoneElse(formData) && !isTicketOrder;
  const recipientName = showRecipientStep || pickupOther ? nameOf(formData.recipient) : '';
  const peopleText = customerName
    ? (recipientName && recipientName !== customerName ? `${customerName} → ${recipientName}` : customerName)
    : (recipientName ? `→ ${recipientName}` : 'No customer yet');
  const dateText = formData.delivery.date
    ? new Date(`${formData.delivery.date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    : '';
  const clockText = (hhmm) => new Date(`2000-01-01T${hhmm}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const TIME_WORDS = { before: 'by', after: 'after', exact: 'at' };
  const timeText = (() => {
    const d = formData.delivery;
    const period = isPickup ? d.pickupTimePeriod : d.timeCondition;
    const at = isPickup ? d.pickupTime : d.priorityTime;
    if (['am', 'pm', 'AM', 'PM'].includes(period)) return period.toUpperCase();
    if (TIME_WORDS[period] && at) return `${TIME_WORDS[period]} ${clockText(at)}`;
    return null;
  })();
  const whereText = isTicketOrder
    ? 'Tickets'
    : isPickup
      ? 'In store'
      : [LOCATION_LABELS[formData.delivery.locationType], deliveryQuote?.zoneName].filter(Boolean).join(' · ');

  // "Before you create": the submit checks, grouped. ok = passes, todo = still
  // needed, warn = not set but fine to leave.
  const checkErrors = validateOrder({ formData, user, isTicketOrder, deliveryFee });
  const hasError = (...paths) => Object.keys(checkErrors).some((k) => paths.some((p) => k === p || k.startsWith(`${p}.`)));
  const itemCount = formData.items.length;
  const checklist = [
    user?.accountType === 'staff' && {
      key: 'staff',
      state: checkErrors.staffMember ? 'todo' : 'ok',
      label: checkErrors.staffMember ? 'Staff member not picked' : 'Staff member picked',
    },
    {
      key: 'people',
      state: hasError('customer', 'recipient') ? 'todo' : 'ok',
      label: showRecipientStep ? 'Customer, recipient and address' : pickupOther ? 'Customer and who picks it up' : 'Customer details',
    },
    {
      key: 'date',
      state: hasError('delivery.date', 'delivery.priorityTime') ? 'todo' : 'ok',
      label: checkErrors['delivery.date']
        ? `${isPickup ? 'Pickup' : 'Delivery'} date not picked`
        : checkErrors['delivery.priorityTime']
          ? 'Specific time not filled in'
          : `${isPickup ? 'Pickup' : 'Delivery'} ${dateText}`,
    },
    {
      key: 'items',
      state: hasError('items') ? 'todo' : 'ok',
      label: hasError('items')
        ? 'Every item needs a name and price'
        : `${itemCount} item${itemCount !== 1 ? 's' : ''}, ${fmtUSD(itemsSubtotal)}`,
    },
    showRecipientStep && {
      key: 'fee',
      state: checkErrors['delivery.fee'] ? 'todo' : 'ok',
      label: checkErrors['delivery.fee'] ? 'Delivery fee not worked out yet' : `Delivery fee ${fmtUSD(deliveryFee)}`,
    },
    !isTicketOrder && !timeText && {
      key: 'time',
      state: 'warn',
      label: 'Time preference not set — fine for most orders',
    },
  ].filter(Boolean);

  // Header autosave indicator: the form's own autosave to this device, or a
  // server save while one is running or just finished.
  const autosave = saveStatus === 'saving'
    ? { dot: 'bg-gray-400', text: 'Saving draft…' }
    : saveStatus === 'error'
      ? { dot: 'bg-danger', text: 'Save failed' }
      : saveStatus === 'saved' && lastServerSaved
        ? { dot: 'bg-dot-ok', text: `Draft saved ${formatTimeInTimezone(lastServerSaved)}` }
        : hasContent && autosavedAt
          ? { dot: 'bg-dot-ok', text: `Draft saved ${formatTimeInTimezone(autosavedAt)}` }
          : null;

  const setFulfillment = (value) => {
    if (formData.delivery.fulfillmentType !== value) {
      handleDeliveryChange({ ...formData.delivery, fulfillmentType: value });
    }
  };

  const fieldClass = (error) => `input-field sm:text-base${error ? ' input-error' : ''}`;

  // Internal order (gallery, marketing, comps): booked on the shop's hidden
  // internal customer, so the customer is cleared. A pickup the customer was
  // collecting now names who collects it instead (kept if this is unticked).
  const setInternalOrder = (internal) => {
    setFormData(prev => {
      if (!internal) return { ...prev, internal: false };
      const customerCollects = prev.delivery?.fulfillmentType === 'pickup' && prev.delivery?.pickupBySomeoneElse !== true;
      return {
        ...prev,
        internal: true,
        customer: { id: null, firstName: '', lastName: '', email: '', phone: '', alternativePhone: undefined, alternativePhoneContext: undefined, defaultAddress: null },
        ...(customerCollects && {
          delivery: { ...prev.delivery, pickupBySomeoneElse: true },
          recipient: { ...prev.recipient, firstName: '', lastName: '', phone: '', alternativePhone: undefined, alternativePhoneContext: undefined },
        }),
      };
    });
    if (internal) {
      customersDbApi.listInternal()
        .then((r) => setInternalNames(Array.isArray(r?.data) ? r.data : []))
        .catch(() => setInternalNames([]));
      setSelectedCustomer(null);
      setCustomerSearch('');
      setShowCustomerDropdown(false);
      setShowPhoneDropdown(false);
      setShowCustomerHistory(false);
      setValidationErrors(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => !k.startsWith('customer.'))));
    }
  };

  // Card message step. Rendered last in Focused, and in the right column under
  // Recipient in Classic, so the DOM order matches what is on screen either way.
  const messageStep = !isTicketOrder && (
    <OrderFormSection
      step={stepNumbers.message}
      title="Card message"
      aside={(
        <span className={`text-xs tabular-nums ${formData.cardMessage.length >= 255 ? 'text-danger-fg' : 'text-gray-500'}`}>
          {formData.cardMessage.length} / 255
        </span>
      )}
    >
      <textarea
        value={formData.cardMessage}
        onChange={(e) => {
          if (e.target.value.length <= 255) {
            setFormData(prev => ({ ...prev, cardMessage: e.target.value }));
          }
        }}
        rows={3}
        tabIndex={900}
        aria-label="Card message"
        className="input-field resize-y px-3.5 py-3 text-base leading-normal sm:text-base"
        placeholder="Message for the card that goes with the flowers"
      />
      <p className="field-hint input-help">Printed exactly as written.</p>
    </OrderFormSection>
  );

  return (
    <>
      {/* Full-bleed page: cancels the route wrapper's padding (App.js) so the
          header band runs edge to edge, as on every Forest page. */}
      <div className="-mx-2 -mt-6 sm:-mx-4 lg:-mx-6">
        <PageHeader
          title="New order"
          backHref="/"
          backLabel="Dashboard"
          actions={(
            <>
              <StaffMemberSelector inline={true} />
              {autosave && (
                <span
                  className="inline-flex items-center gap-2 text-xs text-gray-500"
                  title={autosave.text.startsWith('Draft saved') ? 'Kept on this device. Save draft shares it with the shop.' : undefined}
                  role="status"
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${autosave.dot}`} aria-hidden="true" />
                  {autosave.text}
                </span>
              )}
              <button
                type="button"
                onClick={() => setShowDraftManager(true)}
                className="btn-secondary"
                title={`${draftCounts.total} total draft${draftCounts.total !== 1 ? 's' : ''} (${draftCounts.server} server, ${draftCounts.local} local)`}
              >
                Drafts ({draftCounts.total})
                {hasLocalRecovery() && <span className="font-semibold text-floral-700">· Recovery</span>}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Reset form? This will clear all current data.')) {
                    resetForm();
                  }
                }}
                disabled={!hasContent}
                className="btn-ghost enabled:hover:bg-danger-bg enabled:hover:text-danger-fg"
                title="Clear all form fields"
              >
                Clear form
              </button>
            </>
          )}
        />

        <form ref={formRef} onSubmit={handleSubmit} className="order-entry px-4 pb-10 pt-5 sm:px-7">
          {/* The summary rail keeps the same place and width in both layouts.
              Classic splits the sections into two columns from xl, where each
              column still gets a usable width beside the rail; below that it
              reads as one column like Focused. Column wrappers dissolve
              (display: contents) in Focused so the same markup flows as one. */}
          <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
            <div className={isFocused ? 'flex min-w-0 flex-col gap-4' : 'grid min-w-0 gap-4 xl:grid-cols-2'}>
            <div className={isFocused ? 'contents' : 'flex min-w-0 flex-col gap-4'}>

              {/* 1 Customer */}
              <OrderFormSection
                step={stepNumbers.customer}
                title="Customer"
                aside={formData.internal ? (
                  <span className="inline-flex h-5 items-center rounded-md bg-floral-100 px-2 text-[11px] font-semibold text-floral-900">
                    Internal
                  </span>
                ) : (selectedCustomer?.isHouseAccount || selectedCustomer?.id) && (
                  <>
                    {selectedCustomer?.isHouseAccount && (
                      <span className="inline-flex h-5 items-center rounded-md bg-floral-100 px-2 text-[11px] font-semibold text-floral-900">
                        House account
                      </span>
                    )}
                    {selectedCustomer?.id && (
                      <SectionLink onClick={() => setShowCustomerHistory(true)}>
                        Order history{selectedCustomer.totalOrders > 0 ? ` (${selectedCustomer.totalOrders})` : ''}
                      </SectionLink>
                    )}
                  </>
                )}
              >
                <label className="mb-3.5 flex cursor-pointer items-start gap-2.5 rounded-md border border-gray-200 bg-gray-50 px-3.5 py-3 text-[13px]">
                  <input
                    type="checkbox"
                    checked={formData.internal === true}
                    onChange={(e) => setInternalOrder(e.target.checked)}
                    className="mt-px h-[18px] w-[18px] shrink-0 rounded-[5px] border-gray-300 text-floral-700 focus:ring-floral-700"
                  />
                  <span>
                    <span className="font-medium text-gray-900">Internal order</span>
                    <span className="field-hint block text-xs text-gray-600">Gallery, marketing, comps, shop use. Booked on the shop, no Shopify order, nothing to collect.</span>
                  </span>
                </label>

                {formData.internal ? (
                  <div>
                    <label className="field-label input-label" htmlFor="internal-label">
                      Internal order name <span className="input-required">*</span>
                    </label>
                    <input
                      id="internal-label"
                      name="internalLabel"
                      type="text"
                      list="internal-names"
                      value={formData.internalLabel || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => ({ ...prev, internalLabel: value }));
                        if (value.trim()) setValidationErrors(prev => { const { internalLabel: _gone, ...rest } = prev; return rest; });
                      }}
                      data-field="internalLabel"
                      placeholder={`${shopName} Holiday Gallery`}
                      maxLength={100}
                      className={fieldClass(getFieldError('internalLabel'))}
                      autoComplete="off"
                    />
                    <datalist id="internal-names">
                      {internalNames.map((c) => <option key={c.id} value={c.name} />)}
                    </datalist>
                    {getFieldError('internalLabel') ? (
                      <p className="field-error mt-1 text-xs text-red-600">{getFieldError('internalLabel')}</p>
                    ) : (
                      <span className="field-hint mt-1 block text-xs text-gray-600">
                        Shows as the customer on the ticket and in Orders. Reuse a name to keep an order history together.
                      </span>
                    )}
                  </div>
                ) : (
                <>
                {/* Existing customers surface as you type name / email / phone — no separate search box */}
                <div className="relative" ref={customerSearchRef}>
                  <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 md:grid-cols-2">
                    <div>
                      <label className="field-label input-label" htmlFor="customer-first-name">
                        First name <span className="input-required">*</span>
                      </label>
                      <input
                        id="customer-first-name"
                        type="text"
                        value={formData.customer.firstName}
                        onChange={(e) => { handleInputChange('customer', 'firstName', e.target.value); setCustomerSearch(`${e.target.value} ${formData.customer.lastName}`.trim()); }}
                        onKeyDown={handleCustomerSearchKeyDown}
                        onFocus={() => customerSearch.length >= 2 && customers.length > 0 && setShowCustomerDropdown(true)}
                        data-field="customer.firstName"
                        tabIndex={1}
                        className={fieldClass(getFieldError('customer.firstName'))}
                        required
                      />
                      {getFieldError('customer.firstName') && (
                        <p className="input-error-text">{getFieldError('customer.firstName')}</p>
                      )}
                    </div>
                    <div>
                      <label className="field-label input-label" htmlFor="customer-last-name">
                        Last name <span className="input-required">*</span>
                      </label>
                      <input
                        id="customer-last-name"
                        type="text"
                        value={formData.customer.lastName}
                        onChange={(e) => { handleInputChange('customer', 'lastName', e.target.value); setCustomerSearch(`${formData.customer.firstName} ${e.target.value}`.trim()); }}
                        onKeyDown={handleCustomerSearchKeyDown}
                        onFocus={() => customerSearch.length >= 2 && customers.length > 0 && setShowCustomerDropdown(true)}
                        data-field="customer.lastName"
                        tabIndex={2}
                        className={fieldClass(getFieldError('customer.lastName'))}
                        required
                      />
                      {getFieldError('customer.lastName') && (
                        <p className="input-error-text">{getFieldError('customer.lastName')}</p>
                      )}
                    </div>
                  </div>

                  {showCustomerDropdown && customers.length > 0 && (
                    <div
                      ref={customerDropdownRef}
                      className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg"
                    >
                      {customers.map((customer, index) => (
                        <div
                          key={customer.id}
                          onClick={() => handleCustomerSelect(customer)}
                          className={`cursor-pointer px-3 py-2 hover:bg-floral-50 ${
                            index === selectedCustomerIndex ? 'bg-floral-50' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div className="font-medium text-gray-900">
                              {customer.firstName} {customer.lastName}
                            </div>
                            {customer.isHouseAccount && (
                              <span className="inline-flex h-5 items-center rounded-md bg-floral-100 px-2 text-[11px] font-semibold text-floral-900">
                                House account
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {customer.email}
                            {!customer.isHouseAccount && customer.email && customer.phone && <span className="mx-2">•</span>}
                            {!customer.isHouseAccount && customer.phone && <span className="font-semibold tabular-nums">{customer.phone}</span>}
                          </div>
                          {customer.totalOrders > 0 && (
                            <div className="text-xs text-gray-500">
                              {customer.totalOrders} previous orders
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-3.5 grid grid-cols-1 gap-x-4 gap-y-3.5 md:grid-cols-2">
                  <div className="relative">
                    <label className="field-label input-label" htmlFor="customer-phone">
                      Phone <span className="input-required">*</span>
                    </label>
                    <input
                      id="customer-phone"
                      ref={phoneInputRef}
                      type="tel"
                      value={formData.customer.phone}
                      onChange={(e) => handleInputChange('customer', 'phone', e.target.value)}
                      onKeyDown={handlePhoneSearchKeyDown}
                      onFocus={() => {
                        const digits = formData.customer.phone.replace(/\D/g, '');
                        if (digits.length >= 3 && phoneCustomers.length > 0) {
                          setShowPhoneDropdown(true);
                        }
                      }}
                      data-field="customer.phone"
                      tabIndex={3}
                      readOnly={isHouseAccount}
                      aria-describedby={isHouseAccount ? 'customer-phone-hint' : undefined}
                      className={`${fieldClass(getFieldError('customer.phone'))} tabular-nums${isHouseAccount ? ' bg-gray-50' : ''}`}
                      required
                    />
                    {getFieldError('customer.phone') ? (
                      <p className="input-error-text">{getFieldError('customer.phone')}</p>
                    ) : isHouseAccount && (
                      <span id="customer-phone-hint" className="input-help">Account number. Enter the caller under Placed by.</span>
                    )}

                    {showPhoneDropdown && phoneCustomers.length > 0 && (
                      <div
                        ref={phoneDropdownRef}
                        className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg"
                      >
                        {phoneCustomers.map((customer, index) => (
                          <div
                            key={customer.id}
                            onClick={() => handlePhoneCustomerSelect(customer)}
                            className={`cursor-pointer px-3 py-2 hover:bg-floral-50 ${
                              index === selectedPhoneIndex ? 'bg-floral-50' : ''
                            }`}
                          >
                            <div className="font-medium text-gray-900">
                              {customer.firstName} {customer.lastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {customer.email}
                              {customer.email && customer.phone && <span className="mx-2">•</span>}
                              {customer.phone && <span className="font-semibold tabular-nums">{customer.phone}</span>}
                            </div>
                            {customer.totalOrders > 0 && (
                              <div className="text-xs text-gray-500">
                                {customer.totalOrders} previous orders
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {formData.customer.alternativePhone === undefined && (
                      <SectionLink
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          customer: { ...prev.customer, alternativePhone: '' }
                        }))}
                        className="mt-1.5 !text-xs"
                        tabIndex={5}
                      >
                        + Alternative number
                      </SectionLink>
                    )}
                  </div>
                  <div>
                    <label className="field-label input-label" htmlFor="customer-email">
                      Email <span className="field-hint font-normal text-gray-500">· optional</span>
                    </label>
                    <input
                      id="customer-email"
                      type="email"
                      value={formData.customer.email}
                      onChange={(e) => { handleInputChange('customer', 'email', e.target.value); setCustomerSearch(e.target.value); }}
                      onKeyDown={handleCustomerSearchKeyDown}
                      data-field="customer.email"
                      tabIndex={4}
                      placeholder="For the receipt"
                      className={fieldClass(getFieldError('customer.email'))}
                    />
                    {getFieldError('customer.email') && (
                      <p className="input-error-text">{getFieldError('customer.email')}</p>
                    )}
                  </div>

                  {/* Alternative phone number (conditional) */}
                  {formData.customer.alternativePhone !== undefined && (
                    <>
                      <div>
                        <div className="flex items-center justify-between">
                          <label className="field-label input-label" htmlFor="customer-alt-phone">
                            Alternative phone
                          </label>
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              customer: {
                                ...prev.customer,
                                alternativePhone: undefined,
                                alternativePhoneContext: undefined
                              }
                            }))}
                            className="mb-1.5 text-xs font-medium text-gray-500 hover:text-danger-fg"
                            tabIndex={7}
                          >
                            Remove
                          </button>
                        </div>
                        <input
                          id="customer-alt-phone"
                          type="tel"
                          value={formData.customer.alternativePhone || ''}
                          onChange={(e) => handleInputChange('customer', 'alternativePhone', e.target.value)}
                          className={`${fieldClass(false)} tabular-nums`}
                          placeholder="(555) 987-6543"
                          maxLength="14"
                          tabIndex={6}
                        />
                      </div>
                      <div>
                        <label className="field-label input-label" htmlFor="customer-alt-context">
                          Context <span className="field-hint font-normal text-gray-500">· optional</span>
                        </label>
                        <input
                          id="customer-alt-context"
                          type="text"
                          value={formData.customer.alternativePhoneContext || ''}
                          onChange={(e) => handleInputChange('customer', 'alternativePhoneContext', e.target.value)}
                          className={fieldClass(false)}
                          placeholder="e.g., cell phone, work, spouse"
                          maxLength="50"
                          tabIndex={7}
                        />
                      </div>
                    </>
                  )}

                  {/* Placed by: who called for the house account. */}
                  {isHouseAccount && (
                    <div className="md:col-span-2">
                      <label className="field-label input-label" htmlFor="customer-contact">
                        Placed by <span className="input-required">*</span>
                      </label>
                      <select
                        id="customer-contact"
                        value={placedBySelectValue}
                        onChange={(e) => choosePlacedBy(e.target.value)}
                        data-field="customer.contact"
                        tabIndex={8}
                        className={fieldClass(getFieldError('customer.contact'))}
                      >
                        <option value="">Choose who called…</option>
                        {contactOptions.map((c) => (
                          <option key={c.id} value={String(c.id)}>{contactOptionLabel(c)}</option>
                        ))}
                        <option value={NEW_CONTACT}>Someone new…</option>
                      </select>
                      {getFieldError('customer.contact') && (
                        <p className="input-error-text">{getFieldError('customer.contact')}</p>
                      )}
                      {!isNewContact && showMakeDefault && (
                        <label className="mt-2 flex cursor-pointer items-center gap-2.5 text-[13px] text-gray-900">
                          <input
                            type="checkbox"
                            checked={placedBy.isDefault === true}
                            onChange={(e) => setPlacedByDefault(e.target.checked)}
                            tabIndex={9}
                            className="h-[18px] w-[18px] shrink-0 rounded-[5px] border-gray-300 text-floral-700 focus:ring-floral-700"
                          />
                          Make this the default contact for {accountName || 'this account'}
                        </label>
                      )}

                      {isNewContact && (
                        <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-3.5 md:grid-cols-2">
                          <div>
                            <label className="field-label input-label" htmlFor="contact-first-name">
                              First name <span className="input-required">*</span>
                            </label>
                            <input
                              id="contact-first-name"
                              type="text"
                              value={placedBy.firstName || ''}
                              onChange={(e) => setNewContactField('firstName', e.target.value)}
                              data-field="customer.contact.firstName"
                              tabIndex={9}
                              className={fieldClass(getFieldError('customer.contact.firstName'))}
                              autoComplete="off"
                            />
                            {getFieldError('customer.contact.firstName') && (
                              <p className="input-error-text">{getFieldError('customer.contact.firstName')}</p>
                            )}
                          </div>
                          <div>
                            <label className="field-label input-label" htmlFor="contact-last-name">
                              Last name <span className="field-hint font-normal text-gray-500">· optional</span>
                            </label>
                            <input
                              id="contact-last-name"
                              type="text"
                              value={placedBy.lastName || ''}
                              onChange={(e) => setNewContactField('lastName', e.target.value)}
                              tabIndex={9}
                              className={fieldClass(false)}
                              autoComplete="off"
                            />
                          </div>
                          <div>
                            <label className="field-label input-label" htmlFor="contact-phone">
                              Phone
                            </label>
                            <input
                              id="contact-phone"
                              type="tel"
                              value={placedBy.phone || ''}
                              onChange={(e) => setNewContactField('phone', e.target.value)}
                              data-field="customer.contact.phone"
                              tabIndex={9}
                              className={`${fieldClass(getFieldError('customer.contact.phone'))} tabular-nums`}
                              autoComplete="off"
                            />
                            {getFieldError('customer.contact.phone') && (
                              <p className="input-error-text">{getFieldError('customer.contact.phone')}</p>
                            )}
                          </div>
                          <div>
                            <label className="field-label input-label" htmlFor="contact-email">
                              Email
                            </label>
                            <input
                              id="contact-email"
                              type="email"
                              value={placedBy.email || ''}
                              onChange={(e) => setNewContactField('email', e.target.value)}
                              data-field="customer.contact.email"
                              tabIndex={9}
                              className={fieldClass(getFieldError('customer.contact.email'))}
                              autoComplete="off"
                            />
                            {getFieldError('customer.contact.email') && (
                              <p className="input-error-text">{getFieldError('customer.contact.email')}</p>
                            )}
                          </div>
                          <div>
                            <label className="field-label input-label" htmlFor="contact-title">
                              Title <span className="field-hint font-normal text-gray-500">· optional</span>
                            </label>
                            <input
                              id="contact-title"
                              type="text"
                              value={placedBy.title || ''}
                              onChange={(e) => setNewContactField('title', e.target.value)}
                              tabIndex={9}
                              className={fieldClass(false)}
                              autoComplete="off"
                            />
                          </div>
                          {showMakeDefault && (
                            <label className="flex cursor-pointer items-center gap-2.5 text-[13px] text-gray-900 md:col-span-2">
                              <input
                                type="checkbox"
                                checked={placedBy.isDefault === true}
                                onChange={(e) => setPlacedByDefault(e.target.checked)}
                                tabIndex={9}
                                className="h-[18px] w-[18px] shrink-0 rounded-[5px] border-gray-300 text-floral-700 focus:ring-floral-700"
                              />
                              Make this the default contact for {accountName || 'this account'}
                            </label>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Order-update text consent. The box stays unchecked unless
                      the customer says yes. */}
                  <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-gray-200 bg-gray-50 px-3.5 py-3 text-[13px] md:col-span-2">
                    <input
                      type="checkbox"
                      checked={smsConsent}
                      onChange={(e) => {
                        smsConsentSetForRef.current = customerPhoneDigits;
                        setSmsConsent(e.target.checked);
                      }}
                      className="mt-px h-[18px] w-[18px] shrink-0 rounded-[5px] border-gray-300 text-floral-700 focus:ring-floral-700"
                    />
                    <span>
                      <span className="font-medium text-gray-900">Customer said yes to text updates</span>
                      <span className="field-hint block text-xs text-gray-600">Out for delivery, ready for pickup, or if we can't deliver. No marketing.</span>
                    </span>
                  </label>
                </div>
                </>
                )}
              </OrderFormSection>

              {/* 2 Delivery or pickup — the choice lives in the step header */}
              <OrderFormSection
                step={stepNumbers.fulfil}
                title="Delivery or pickup"
                aside={!isTicketOrder && (
                  <div className="seg fulfil-choices" role="radiogroup" aria-label="Delivery or pickup">
                    {[{ value: 'delivery', label: 'Local delivery' }, { value: 'pickup', label: 'Store pickup' }].map((opt) => {
                      const selected = formData.delivery.fulfillmentType === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          onClick={() => setFulfillment(opt.value)}
                          tabIndex={opt.value === 'delivery' ? 8 : 9}
                          className={`seg-item fulfil-choice h-[calc(var(--control-h-sm)-2px)] ${selected ? 'seg-item-active is-selected' : ''}`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              >
                {isTicketOrder ? (
                  <p className="text-sm text-gray-700">
                    Tickets only. No recipient, card message or delivery needed, and no work ticket prints.
                    The order is saved as already collected.
                  </p>
                ) : (
                  <DeliverySection
                    layout="forest"
                    deliveryData={formData.delivery}
                    onDeliveryChange={handleDeliveryChange}
                    onDeliveryFeeChange={handleDeliveryFeeChange}
                    onPriorityTimeChange={handlePriorityTimeFeeChange}
                    onDeliveryQuote={setDeliveryQuote}
                    onFeeStatus={setDeliveryFeeStatus}
                    itemsLeadTimeDays={itemsLeadTimeDays}
                    validationErrors={validationErrors}
                    recipientAddress={formData.recipient}
                    tabIndex={11}
                    isHouseAccount={formData.customer?.isHouseAccount || false}
                  />
                )}
                {isPickup && !isTicketOrder && (
                  <PickupPersonRow
                    customer={formData.customer}
                    recipient={formData.recipient}
                    someoneElse={pickupOther}
                    internal={formData.internal === true}
                    validationErrors={validationErrors}
                    onSomeoneElse={() => setFormData(prev => ({
                      ...prev,
                      delivery: { ...prev.delivery, pickupBySomeoneElse: true },
                      recipient: { ...prev.recipient, firstName: '', lastName: '', phone: '', alternativePhone: undefined, alternativePhoneContext: undefined },
                    }))}
                    onSameAsCustomer={() => setFormData(prev => ({
                      ...prev,
                      delivery: { ...prev.delivery, pickupBySomeoneElse: false },
                    }))}
                    onChange={(field, value) => setFormData(prev => ({
                      ...prev,
                      recipient: { ...prev.recipient, [field]: value },
                    }))}
                  />
                )}
              </OrderFormSection>
            </div>

            <div className={isFocused ? 'contents' : 'flex min-w-0 flex-col gap-4'}>
              {/* 3 Recipient — delivery only */}
              {showRecipientStep && (
                <OrderFormSection
                  step={stepNumbers.recipient}
                  title="Recipient"
                  aside={(
                    <>
                      <SectionLink
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            recipient: {
                              ...prev.recipient,
                              firstName: prev.customer.firstName,
                              lastName: prev.customer.lastName,
                              phone: prev.customer.phone,
                            }
                          }));
                          onNotification?.('Recipient set to the customer', 'info', 2000);
                        }}
                        disabled={!formData.customer.firstName && !formData.customer.lastName && !formData.customer.phone}
                      >
                        Same as customer
                      </SectionLink>
                      <span className="text-gray-300" aria-hidden="true">·</span>
                      <SectionLink
                        onClick={() => {
                          handleRecipientChange({
                            firstName: '',
                            lastName: '',
                            businessName: '',
                            address1: '',
                            address2: '',
                            city: '',
                            state: '',
                            zip: '',
                            phone: ''
                          });
                          onNotification?.('Recipient information cleared', 'info', 2000);
                        }}
                      >
                        Clear
                      </SectionLink>
                    </>
                  )}
                  footer={(
                    <DeliveryFeeStrip
                      status={deliveryFeeStatus}
                      effectiveFee={deliveryFee}
                      locationLabel={LOCATION_LABELS[formData.delivery.locationType]}
                      override={formData.delivery.deliveryFeeOverride || ''}
                      onOverrideChange={(value) => handleDeliveryChange({ ...formData.delivery, deliveryFeeOverride: value })}
                    />
                  )}
                >
                  <RecipientSection
                    layout="forest"
                    recipient={formData.recipient}
                    onRecipientChange={handleRecipientChange}
                    customer={formData.customer}
                    customerLocalId={selectedCustomer?.localCustomerId}
                    sameAsCustomer={false}
                    validationErrors={validationErrors}
                    onNotification={onNotification}
                    fulfillmentType={formData.delivery?.fulfillmentType}
                    deliveryInstructions={formData.delivery.locationInstructions || ''}
                    onDeliveryInstructionsChange={(value) => handleDeliveryChange({ ...formData.delivery, locationInstructions: value })}
                    tabIndex={30}
                  />
                </OrderFormSection>
              )}
              {!isFocused && messageStep}
            </div>

            <div className={isFocused ? 'contents' : 'min-w-0 xl:col-span-2'}>
              {/* 4 Items */}
              <ItemsSection
                layout="forest"
                step={stepNumbers.items}
                items={formData.items}
                onItemsChange={handleItemsChange}
                validationErrors={validationErrors}
                onNotification={onNotification}
                deliveryFee={deliveryFee}
                priorityTimeFee={priorityTimeFee}
                salesTaxRate={settings.salesTaxRate ?? 0}
                salesTaxRateNotSet={settings.salesTaxRate == null}
                taxQuote={stripeTaxMode ? taxQuote : null}
                isTaxExempt={formTaxExempt}
                showLowStockWarnings={settings.showLowStockWarnings !== false}
                tabIndex={50}
                showTotals={false}
              />
            </div>

              {isFocused && messageStep}
            </div>

            {/* Summary rail: totals, the one primary action, then the checklist */}
            <aside className="flex flex-col gap-4 lg:sticky lg:top-5">
              <div className="rounded-lg border border-gray-200 bg-white">
                <div className="rounded-t-lg border-b border-gray-200 bg-floral-50 px-[18px] py-3 text-[15px] font-semibold text-gray-900">
                  Order summary
                </div>
                <div className="flex flex-col gap-3 px-[18px] py-4 text-[13px]">
                  <div className="flex flex-col gap-1.5 text-gray-600">
                    <div className="break-words">{peopleText}</div>
                    <div className="flex justify-between gap-3">
                      <span>{dateText || 'No date yet'}{timeText ? ` · ${timeText}` : isPickup || isTicketOrder ? '' : ' · no window'}</span>
                      {whereText && <span className="text-right text-gray-900">{whereText}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 border-t border-gray-200 pt-3">
                    {formData.items.map((item, index) => (
                      <div key={item.id || index} className="flex justify-between gap-3 text-gray-600">
                        <span className="min-w-0 break-words">
                          {(item.name || '').trim() || `Item ${index + 1}`} × {parseInt(item.quantity) || 0}
                        </span>
                        <span className="tabular-nums text-gray-900">
                          {fmtUSD((parseFloat(item.price) || 0) * (parseInt(item.quantity) || 0))}
                        </span>
                      </div>
                    ))}
                    {!isTicketOrder && (
                      <div className="flex justify-between gap-3 text-gray-600">
                        <span>{isPickup ? 'Pickup' : `Delivery${deliveryQuote?.zoneName ? ` · ${deliveryQuote.zoneName}` : ''}`}</span>
                        <span className="tabular-nums text-gray-900">{fmtUSD(deliveryFee)}</span>
                      </div>
                    )}
                    {priorityTimeFee > 0 && (
                      <div className="flex justify-between gap-3 text-gray-600">
                        <span>Priority time</span>
                        <span className="tabular-nums text-gray-900">{fmtUSD(priorityTimeFee)}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-3 text-gray-600">
                      <span className="flex flex-wrap items-center gap-2">
                        <span>{formTaxExempt || summaryTaxRate == null ? 'Tax' : `Tax (${formatTaxRate(summaryTaxRate)})`}</span>
                        {!formTaxExempt && !stripeTaxMode && settings.salesTaxRate == null && <SalesTaxNotSetChip />}
                        <button
                          type="button"
                          role="switch"
                          aria-checked={formTaxExempt}
                          onClick={() => {
                            // Turning exempt ON asks how (this order / the profile); OFF just sets the override
                            if (!formTaxExempt) setShowTaxExemptModal(true);
                            else setTaxExemptOverride(false);
                          }}
                          className="inline-flex items-center gap-1.5 text-xs"
                        >
                          <span className={`relative inline-block h-4 w-7 rounded-full transition-colors ${formTaxExempt ? 'bg-floral-700' : 'bg-gray-300'}`}>
                            <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-[left] ${formTaxExempt ? 'left-[14px]' : 'left-0.5'}`} />
                          </span>
                          Exempt
                        </button>
                      </span>
                      <span className="tabular-nums text-gray-900">{summaryTax == null ? taxNotYet : fmtUSD(summaryTax)}</span>
                    </div>
                  </div>
                  <div className="flex items-baseline justify-between border-t border-gray-200 pt-3">
                    <span className="text-[15px] font-semibold text-gray-900">Total</span>
                    <span className="text-[28px] font-semibold tracking-tight tabular-nums text-gray-900">
                      {summaryTax == null ? taxNotYet : fmtUSD(itemsSubtotal + deliveryFee + priorityTimeFee + summaryTax)}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-2 px-[18px] pb-[18px]">
                  <ValidationSummary />
                  <button
                    type="button"
                    onClick={(e) => handleSubmit(e)}
                    disabled={loading}
                    className="btn-primary btn-lg w-full"
                  >
                    {loading
                      ? 'Creating order…'
                      : formData.internal
                        ? 'Create internal order'
                        : isHouseAccount
                        // House accounts are billed later: no payment is taken here.
                        ? 'Create order'
                        : 'Create order · take payment'}
                  </button>
                  <div className={`grid gap-2 ${isPickup && !isTicketOrder && !formData.internal ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    {/* Pay later & print — pickup orders only, as before; internal orders have nothing to collect */}
                    {isPickup && !isTicketOrder && !formData.internal && (
                      <button
                        type="button"
                        onClick={(e) => handleSubmit(e, 'online_store', true)}
                        disabled={loading}
                        className="btn-secondary w-full px-2"
                      >
                        Pay later &amp; print
                      </button>
                    )}
                    <div className="[&>div>button:first-child]:flex-1 [&>div>button:last-child]:border-l-0 [&>div]:flex [&>div]:w-full">
                      <SplitButton
                        label="Save draft"
                        onClick={() => saveToServer(formData, 'Manual save')}
                        disabled={!hasContent || isSaving || isPrintingDraft}
                        className="h-ctl justify-center border !border-gray-300 bg-white text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
                        title="Save current form to server as draft"
                        loading={isSaving}
                        menuItems={[
                          {
                            label: 'Save & Print',
                            onClick: handleSaveDraftAndPrint,
                            disabled: isPrintingDraft,
                          },
                          {
                            label: 'Preview Email',
                            onClick: handlePreviewDraftInvoice,
                            disabled: isLoadingDraftPreview || isSendingDraftEmail,
                          },
                          {
                            label: 'Save & Email',
                            onClick: handleEmailDraftInvoice,
                            disabled: isSendingDraftEmail || !formData.customer.email,
                          },
                          {
                            label: 'Save, Print & Email',
                            onClick: handleSaveDraftPrintAndEmail,
                            disabled: isPrintingDraft || isSendingDraftEmail || !formData.customer.email,
                          }
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-1.5 rounded-lg border border-gray-200 bg-white px-[18px] py-3.5 text-xs text-gray-600">
                <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">Before you create</div>
                {checklist.map((row) => (
                  <div key={row.key} className="flex items-center gap-2">
                    {row.state === 'ok' ? (
                      <CheckIcon size={12} strokeWidth={3} className="shrink-0 text-dot-ok" />
                    ) : (
                      <span
                        className={`inline-block h-3 w-3 shrink-0 rounded-full border-[1.5px] ${row.state === 'warn' ? 'border-dot-yellow' : 'border-gray-400'}`}
                        aria-hidden="true"
                      />
                    )}
                    <span className={row.state === 'todo' ? 'text-gray-900' : ''}>
                      <span className="sr-only">{row.state === 'ok' ? 'Done: ' : row.state === 'warn' ? 'Optional: ' : 'To do: '}</span>
                      {row.label}
                    </span>
                  </div>
                ))}
              </div>
            </aside>
          </div>
        </form>
      </div>

      {/* Convert to Special Event Modal — Shelved, will revisit */}
      {/* {showConvertToEventModal && (
        <ConvertToEventModal
          formData={formData}
          onClose={() => setShowConvertToEventModal(false)}
          onNotification={onNotification}
        />
      )} */}

      {/* Order Creation Progress Overlay */}
      <OrderCreationProgress
        isVisible={showCreationProgress}
        withShopifyStep={!counterPayment}
        onComplete={() => {
          // Progress animation complete - overlay will hide when API returns
        }}
      />

      <OrderCreatedModal
        open={showSuccessModal}
        orderSuccess={orderSuccess}
        onDone={finishOrderCreated}
        onOpenInvoice={openInvoiceUrl}
      />

      {/* Backdated House Account Order Confirmation */}
      <Modal
        isOpen={showBackdatedConfirm}
        onClose={() => {
          setShowBackdatedConfirm(false);
          pendingBackdatedSubmitRef.current = null;
        }}
        type="warning"
        title="Confirm Backdated Order"
        message={`Delivery date ${formData.delivery?.date} is in the past. This will create a backdated house account order for ${formData.customer?.firstName || ''} ${formData.customer?.lastName || ''} that can be billed for past service. Continue?`}
        confirmText="Yes, Create Backdated Order"
        cancelText="Cancel"
        showCancel={true}
        onConfirm={() => {
          setShowBackdatedConfirm(false);
          const args = pendingBackdatedSubmitRef.current;
          pendingBackdatedSubmitRef.current = null;
          if (args) {
            skipBackdatedCheckRef.current = true;
            handleSubmit({ preventDefault: () => {} }, args.source, args.printedUnpaid);
          }
        }}
        onCancel={() => {
          setShowBackdatedConfirm(false);
          pendingBackdatedSubmitRef.current = null;
        }}
      />

      {/* Tax Exempt Confirmation Modal */}
      {showTaxExemptModal && (
        <div className="fixed inset-0 bg-[color:var(--c-backdrop)] flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Tax Exempt</h3>
            <p className="text-sm text-gray-600 mb-4">
              How should tax exemption be applied?
            </p>
            {selectedCustomer?.id && (
              <p className="text-sm text-gray-500 mb-4">
                Customer: <span className="font-medium">{selectedCustomer.firstName} {selectedCustomer.lastName}</span>
              </p>
            )}
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  setTaxExemptOverride(true);
                  setShowTaxExemptModal(false);
                }}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                This order only
              </button>
              <button
                type="button"
                disabled={!selectedCustomer?.id}
                onClick={async () => {
                  setTaxExemptOverride(true);
                  setShowTaxExemptModal(false);
                  try {
                    await customersDbApi.updateTaxExempt(selectedCustomer.id, true);
                    setSelectedCustomer(prev => ({ ...prev, taxExempt: true }));
                    onNotification?.('Customer marked as permanently tax exempt', 'success');
                  } catch (err) {
                    onNotification?.('Tax exempt applied to this order. Failed to save to profile: ' + err.message, 'warning');
                  }
                }}
                className={`w-full px-4 py-2 text-sm font-medium rounded-md ${selectedCustomer?.id ? 'text-white bg-floral-700 hover:bg-floral-800' : 'text-gray-400 bg-gray-100 cursor-not-allowed'}`}
              >
                Permanently (save to profile)
              </button>
              {!selectedCustomer?.id && (
                <p className="text-xs text-gray-400 -mt-1 text-center">Select a customer first</p>
              )}
              <button
                type="button"
                onClick={() => setShowTaxExemptModal(false)}
                className="w-full px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hybrid Draft Manager Modal */}
      {showDraftManager && (
        <div className="fixed inset-0 bg-[color:var(--c-backdrop)] flex items-center justify-center z-50 p-4">
          <HybridDraftManager
            serverDrafts={serverDrafts}
            localDraft={localDraft}
            onLoadServerDraft={async (draftId) => {
              const result = await loadServerDraft(draftId);
              if (result.success) {
                const loadedData = result.draft.form_data;
                setFormData(loadedData);
                // Restore saved fees from draft (falls back to 0 if not saved)
                setDeliveryFee(savedFeeFor(loadedData));
                setPriorityTimeFee(loadedData.delivery?.savedPriorityTimeFee || 0);
                setShowDraftManager(false);

                // Re-lookup customer to get full data (house account status, internal UUID, tax exempt, etc.)
                // Draft may have stale or Shopify IDs that won't match our internal customer records
                const customerName = `${loadedData.customer?.firstName || ''} ${loadedData.customer?.lastName || ''}`.trim();
                if (customerName) {
                  setCustomerSearch(customerName);
                  try {
                    const response = await searchOrderCustomers(customerName);
                    if (response.success && response.data?.length > 0) {
                      const exactMatch = response.data.find(c =>
                        c.firstName?.toLowerCase() === loadedData.customer?.firstName?.toLowerCase() &&
                        c.lastName?.toLowerCase() === loadedData.customer?.lastName?.toLowerCase()
                      );
                      if (exactMatch) {
                        setSelectedCustomer(exactMatch);
                        setFormData(prev => ({
                          ...prev,
                          customer: { ...prev.customer, id: exactMatch.id, isHouseAccount: exactMatch.isHouseAccount || false },
                        }));
                      }
                    }
                  } catch (err) {
                    console.warn('[Draft Load] Customer re-lookup failed:', err);
                  }
                }
              }
            }}
            onLoadLocalDraft={() => {
              const result = loadLocalDraft();
              if (result.success) {
                const loadedData = result.draft.data;
                setFormData(loadedData);
                // Restore saved fees from draft (falls back to 0 if not saved)
                setDeliveryFee(savedFeeFor(loadedData));
                setPriorityTimeFee(loadedData.delivery?.savedPriorityTimeFee || 0);
                setShowDraftManager(false);
              }
            }}
            onDeleteServerDraft={deleteServerDraft}
            onClearLocal={() => {
              clearLocal();
              setShowLocalRecovery(false);
            }}
            onSaveToServer={saveToServer}
            onClose={() => setShowDraftManager(false)}
            currentFormData={formData}
            loading={draftsLoading}
          />
        </div>
      )}

      {/* Local Recovery Dialog */}
      {showLocalRecovery && hasLocalRecovery() && (
        <div className="fixed inset-0 bg-[color:var(--c-backdrop)] flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-floral-100 rounded-full flex items-center justify-center mr-3">
                <svg className="w-6 h-6 text-floral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-floral-900">Unsaved Work Detected</h3>
                <p className="text-floral-600 text-sm">We found an auto-saved draft from your last session</p>
              </div>
            </div>

            <div className="bg-floral-50 p-3 rounded-md mb-4">
              <p className="text-sm text-floral-800">
                {localDraft?.data?.customerName && `Customer: ${localDraft.data.customerName}`}
                {localDraft?.data?.customerPhone && ` • ${localDraft.data.customerPhone}`}
              </p>
              <p className="field-hint text-xs text-floral-600 mt-1">
                Last saved: {localDraft ? formatDateTimeInTimezone(localDraft.timestamp) : 'Unknown'}
              </p>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowLocalRecovery(false);
                  clearLocal();
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Dismiss
              </button>
              <button
                onClick={() => {
                  const result = loadLocalDraft();
                  if (result.success) {
                    // Restore saved fees from draft (falls back to 0 if not saved)
                    const data = result.draft?.data;
                    setDeliveryFee(savedFeeFor(data));
                    setPriorityTimeFee(data?.delivery?.savedPriorityTimeFee || 0);
                  }
                  setShowLocalRecovery(false);
                }}
                className="px-4 py-2 bg-floral-700 text-white rounded-md hover:bg-floral-800 transition-colors text-sm"
              >
                Recover Work
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* POS Instructions Modal */}
      <POSInstructions
        isOpen={showPOSInstructions}
        orderData={orderSuccess ? {
          orderNumber: orderSuccess.draftOrderId || orderSuccess.orderName,
          customerName: `${formData.customer.firstName} ${formData.customer.lastName}`.trim(),
          customerEmail: formData.customer.email,
          total: orderSuccess.total,
          itemCount: formData.items?.length || 0,
          createdAt: getCurrentTimeInTimezone(),
          ...orderSuccess
        } : null}
        onClose={() => setShowPOSInstructions(false)}
        onPrint={() => {
          // POS instructions printed
        }}
      />

      {/* Staff Member Modal - Shows after order completion for staff accounts */}
      <StaffMemberModal
        isOpen={showStaffModal}
        onClose={() => {
          setShowStaffModal(false);
          resetForm();
        }}
        onSelect={(staffMember) => {
          // Staff member updated, notification handled by modal
          resetForm();
        }}
        title="Creating next order as"
        subtitle="Press Enter to confirm or select a different staff member"
      />

      <CustomerHistoryModal
        isOpen={showCustomerHistory}
        onClose={() => setShowCustomerHistory(false)}
        customerId={selectedCustomer?.localCustomerId}
        customerName={selectedCustomer ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}` : ''}
        onReorder={handleReorderFromHistory}
      />

      {/* Draft Invoice Email Preview Modal */}
      {showDraftEmailPreview && (
        <div className="fixed inset-0 bg-[color:var(--c-backdrop)] flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Draft Estimate Preview</h3>
              <p className="text-sm text-gray-500">
                {formData.customer?.email ? `To: ${formData.customer.email}` : 'No customer email set'}
              </p>
            </div>
            <div className="flex-1 overflow-y-auto p-0">
              <iframe
                srcDoc={draftEmailPreviewHtml}
                title="Draft Estimate Preview"
                className="w-full border-0"
                style={{ height: '60vh' }}
                sandbox="allow-same-origin"
              />
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button
                type="button"
                onClick={() => { setShowDraftEmailPreview(false); setDraftEmailPreviewHtml(''); }}
                disabled={isSendingDraftEmail}
                className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendDraftFromPreview}
                disabled={isSendingDraftEmail || !formData.customer?.email}
                className="px-4 py-2 text-sm bg-floral-600 text-white rounded hover:bg-floral-700 disabled:opacity-50 flex items-center"
              >
                {isSendingDraftEmail ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Send Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version Info: the shared footer (its own fallback had frozen at v1.2.0; QA A F15) */}
      <VersionFooter />
    </>
  );
});

export default OrderForm;